/**
 * A ficha da Empresa (FR-25, história 11.7) — a mesma tela para criar e para editar.
 *
 * **A ficha do protótipo v2 (realinhada em 28/09/2026): coluna única centralizada, cartões todos
 * abertos, sem abas.** Os cartões, nesta ordem: **Editar empresa / Nova empresa** (CNPJ em linha
 * inteira com a ajuda do que a consulta faz; a grade de duas colunas com nome fantasia e razão
 * social (13.7), listas, o
 * **Responsável** — quem cadastra, na criação; o campo da troca da 2.5, na edição, passado por
 * `responsavel` —, descrição e os campos personalizados), **Informações para contato**, **Dados
 * de endereço** (CEP | Estado · Cidade | Bairro · Rua | Número | Complemento, e os endereços **de
 * entrega** em blocos tracejados), **Pessoas** (inline — a seção da 2.2, que quem monta a tela
 * passa por `pessoas`), **Redes sociais** e o **rodapé** com a **Faixa** Ouro/Prata/Bronze à
 * esquerda (no lugar do "Ranking" de estrelas — decisão 4 da 11.7: a faixa substitui) e
 * Cancelar / Salvar à direita. O que o protótipo tem e o serviço não (WhatsApp, celular, ramal,
 * país, X, referência para o motorista) **não** aparece: a ficha não inventa campo que não grava.
 * O nome fantasia entrou na 13.7.
 *
 * **CNPJ/CPF é o primeiro campo do formulário, e a posição é contrato** (um teste afirma a ordem
 * no DOM). Ao sair dele acontecem **duas consultas, nesta ordem** (FR-24):
 *
 * 1. **O ERP** (5.1, só na criação): CNPJ que já existe **abre a Empresa existente** em vez de criar
 *    duplicada — `aoEncontrarFicha`, e a navegação é de quem montou a tela.
 * 2. **A BrasilAPI**, pelo `cadastro` (AD-28), só para **CNPJ** (14 dígitos — CPF não dispara) e só
 *    quando o ERP disse `inedito` (ou não respondeu: a consulta pública não depende do ERP). Ela
 *    preenche razão social, nome fantasia (13.7) e endereço: **campo vazio é preenchido; campo já
 *    preenchido pede
 *    confirmação** antes de ser sobrescrito. Na edição, ela só sai quando o documento mudou.
 *
 * O **CEP** do endereço (e o de cada entrega) consulta o ViaCEP ao sair do campo, com a mesma regra
 * de preenchimento. **Falha nunca bloqueia** (AD-28): o erro aparece em vermelho **no campo**
 * ("CNPJ não encontrado.", "CEP não encontrado.", "Não foi possível consultar.") e o salvar continua
 * liberado com o que a pessoa digitou. A UF existe no formulário e é editável por isso mesmo.
 *
 * **Editar só monta depois de a ficha chegar.** O `PUT` manda a ficha inteira ("o que não vier
 * volta ao padrão"): um formulário montado vazio enquanto o `GET` viaja descartaria a ficha
 * carregada no primeiro salvar. Os campos personalizados são a exceção — vão como **delta**.
 *
 * **Validação local só do que o cliente já sabe** (documento e razão social); tudo o mais que o
 * servidor recusar aparece verbatim. **Paridade de celular (NFR-1):** sem árvore alternativa; a
 * grade cai para uma coluna por `auto-fit`.
 *
 * **O CNPJ/CPF é opcional desde a 13.1** (FR-24 emendado): vazio, a ficha nasce sem documento
 * (`documento: null`, e não vai ao Carga Máquina) e a consulta ao ERP não sai; preenchido,
 * continua tendo de ter 11 ou 14 dígitos. O `409` da consulta ("ficha vinculada que não está visível para você") **trava** o
 * cadastro daquele documento — liberá-lo como se o ERP estivesse fora criaria a segunda ficha.
 *
 * **A ficha também abre num modal** (13.1): o "Criar nova empresa" do Novo negócio a monta com
 * `valoresIniciais` (o nome fantasia — desde a 13.14a, DW-420 — e o telefone que a pessoa já tinha),
 * `responsavelInicial` (o
 * Responsável escolhido no Negócio, editável aqui — como a porta enxuta fazia) e `aoCancelar`, que
 * troca o link "Cancelar" por um botão. Com `aoCancelar`, sem documento, a ficha avisa que a Empresa
 * de outro responsável pode não aparecer na sugestão (a RLS do FR-30 a esconde) e que o CNPJ resolve.
 * Fora do escopo `todas`, o Responsável do modal é quem cadastra, somente leitura (DW-459, 13.11c).
 *
 * **As Pessoas entram já na ficha nova** (13.2, chamado de 21/10, P13; FR-26 emendado). O cartão
 * Pessoas do "+ Empresa" aceita linhas antes de salvar (`PessoasDaFichaNova.tsx`) — só para quem tem
 * `editar_empresa`, a mesma permissão que a rota de Pessoa exige; sem ela, o cartão diz que as
 * pessoas entram pela ficha, depois. **Desde a 13.14a (DW-379) a Empresa só nasce com tudo certo,
 * inclusive as Pessoas**: a tela confere cada linha antes (o nome, e o mesmo telefone em duas linhas),
 * e um `POST /empresas` só leva a ficha **e** as linhas — o serviço grava tudo de uma vez, e uma linha
 * recusada (`409` `pessoas_com_problema`) não deixa nada. Cada linha recusada fica vermelha com a
 * mensagem do serviço (que diz como resolver); a do contato do WhatsApp com outro nome ganha "Sim, é a
 * mesma pessoa" e "Não, é outra pessoa", e a resposta vai no próximo envio. Saíram a trava, o "Salvar
 * as pessoas" e o "Seguir sem estas pessoas" da 13.2. Um "enviando" síncrono (`ref`) barra o duplo
 * clique.
 *
 * **O telefone tem máscara** (13.2, `@/telefone/mascara-do-telefone`): o campo é `type="tel"`, se
 * forma enquanto se digita e, no `blur` e no envio, passa por `telefoneParaGravar` — só quando mudou
 * em relação ao gravado. O campo de edição mostra o telefone como gravado. O e-mail é `type="email"`;
 * os dois com `autoComplete` por seção (DW-92), e o formulário continua `noValidate`.
 */

import {
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'

import { Link } from '@tanstack/react-router'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useResponsaveis } from '@/modulos/identidade/api'
import { useSessao } from '@/modulos/identidade/sessao'
import { useEhCelular } from '@/shell/Shell'
import { LinhaDoRotulo } from '@/shell/MarcaDeObrigatorio'
import { CAMINHOS } from '@/shell/navegacao'
import { chaveDoTelefone } from '@/telefone/chave-do-telefone'
import { aoDigitarTelefone, telefoneParaGravar } from '@/telefone/mascara-do-telefone'
import {
  type ConsultaDeCep,
  type ConsultaDeCnpj,
  type CorpoDeEndereco,
  type EdicaoDeEmpresa,
  type Empresa,
  type EnderecoDeEntrega,
  type Faixa,
  OPCOES_VAZIAS,
  type OpcaoDeDominio,
  type OpcoesDeDominio,
  type SituacaoDoDocumento,
  type EmpresaCriada,
  camposDaEmpresa,
  etiquetaIdsDe,
  foiAdotada,
  pessoasDaCriada,
  problemasDasPessoas,
  useAcrescentarEnderecoDeEntrega,
  useConsultarCep,
  useConsultarCnpj,
  useConsultarDocumento,
  useCriarEmpresa,
  useEditarEmpresa,
  useEmpresa,
  useOpcoesDeDominio,
  useRemoverEnderecoDeEntrega,
  useNomesDeCamposDesativados,
  useRotulosDeOpcoes,
} from './api'
import {
  nomeExibidoDaEmpresa,
  nomeFantasiaQueConta,
  normalizarParaComparar,
} from './nome-da-empresa'
import { CampoDoGrupo, RECUSA_DO_TEXTO_SEM_GRUPO } from './CampoDoGrupo'
import { EscolhaDeEtiquetas, EtiquetasDoAlvo } from './etiquetas'
import { ROTULOS_DA_FAIXA } from './faixa'
import { corpoDaResposta } from './PerguntaDaAdocao'
import {
  PESSOA_COM_TELEFONE_REPETIDO_NA_LISTA,
  PESSOA_JA_NA_LISTA,
} from './CampoDoNomeComSugestao'
import {
  type PessoaDaFichaNova,
  PessoasDaFichaNova,
  SEM_RECUSA,
  desfazerAEscolha,
  escolherNaLinha,
  linhaQueVai,
  linhaSemNome,
  linhaSemTelefone,
} from './PessoasDaFichaNova'
import {
  type CampoDaRecusa,
  FALTA_NOME,
  FALTA_TELEFONE,
  campoDoCodigo,
  RASCUNHO_VAZIO as PESSOA_VAZIA,
  corpoDe as corpoDaPessoa,
} from './ResponsaveisDaEmpresa'
import './formulario-de-empresa.css'

/** Contagens de dígitos que o serviço aceita: CPF e CNPJ. Espelha o backend, e ele decide. */
const DIGITOS_DO_CPF = 11
const DIGITOS_DO_CNPJ = 14
const DIGITOS_DO_CEP = 8

/**
 * Os tetos de coluna — espelham `TAMANHO_*` de `servicos/cadastro/dominio/`. O servidor continua
 * sendo quem decide; o `maxLength` só evita gastar uma ida à rede com o que o cliente já sabe.
 * `documento` e `cep` usam o teto do campo **digitado**: a pontuação precisa caber.
 */
const TAMANHO_DA_DESCRICAO = 4000
const TAMANHO_DO_DOCUMENTO_DIGITADO = 32
const TAMANHO_DA_RAZAO_SOCIAL = 200
/** O teto do nome fantasia — espelha `TAMANHO_DO_NOME_FANTASIA` de `dominio/empresa.py` (13.7). */
const TAMANHO_DO_NOME_FANTASIA = 200
/** O placeholder do protótipo v2 (`prototipo-v2-2026-09-23.html:1020`), D-1 da 13.7. */
const PLACEHOLDER_DO_NOME_FANTASIA = 'Como vocês chamam a empresa'
const TAMANHO_DO_TELEFONE = 32
const TAMANHO_DO_EMAIL = 320
const TAMANHO_DO_SITE = 255
const TAMANHO_DA_REDE_SOCIAL = 255
const TAMANHO_DO_CEP_DIGITADO = 16
const TAMANHOS_DO_ENDERECO: Record<ParteDoEndereco, number> = {
  cep: TAMANHO_DO_CEP_DIGITADO,
  logradouro: 200,
  numero: 20,
  complemento: 120,
  bairro: 120,
  cidade: 120,
  uf: 2,
}
const TAMANHO_DO_ROTULO_DA_ENTREGA = 80

const DOCUMENTO_INVALIDO = `Informe ${String(DIGITOS_DO_CNPJ)} dígitos para o CNPJ ou ${String(DIGITOS_DO_CPF)} para o CPF.`
const FALTA_RAZAO_SOCIAL = 'Informe a razão social.'
const FALTA_RESPONSAVEL = 'Não foi possível identificar quem está cadastrando. Entre de novo.'

/** Confirmação de que o `PUT` chegou. Sem ela a pessoa reenvia por não saber se gravou. */
const SALVO = 'Ficha salva.'

/** Os textos das Pessoas da ficha nova (13.2, D-10 e D-11; 13.14a). */
export const PESSOA_SEM_NOME = 'Informe o nome de cada pessoa, ou descarte a linha.'
/** Todas as linhas recusadas são de telefone (13.17). */
export const PESSOA_SEM_TELEFONE = 'Informe o telefone de cada pessoa, ou descarte a linha.'
/** A conferência local achou outra linha com problema (13.14a): as linhas dizem qual e como resolver. */
export const PESSOAS_A_CORRIGIR = 'Corrija as pessoas marcadas em vermelho antes de cadastrar.'
/** A linha com o mesmo telefone de outra (13.14a, conferido antes de enviar). */
export function telefoneRepetidoNaFicha(nomeDaOutra: string): string {
  //: O nome sem letra é "um contato sem nome", como no servidor (D-8).
  const pessoa = /\p{L}/u.test(nomeDaOutra) ? `na pessoa ${nomeDaOutra}` : 'em um contato sem nome'
  return `Este telefone já está ${pessoa} desta ficha: corrija o número ou descarte uma das linhas.`
}
export const FICHA_ENCONTRADA_COM_PESSOAS =
  'Este CNPJ/CPF já tem ficha no CRM. As pessoas digitadas aqui ainda não foram gravadas: abra a ficha e cadastre-as lá.'
export const FICHA_ENCONTRADA_COM_PESSOAS_NO_MODAL =
  'Este CNPJ/CPF já tem ficha no CRM. As pessoas digitadas aqui não serão gravadas: use a ficha encontrada e cadastre-as nela depois.'
export const USAR_A_FICHA_ENCONTRADA = 'Usar a ficha encontrada'
export const PESSOAS_SEM_PERMISSAO =
  'As pessoas entram pela ficha da empresa, depois de ela ser cadastrada, por quem pode editá-la.'
export const ABRIR_A_FICHA = 'Abrir a ficha'

/** A permissão que a rota de Pessoa exige — e que mostra as linhas na ficha nova (13.2). */
const PERMISSAO_DAS_PESSOAS = 'editar_empresa'
/** O campo Grupo da ficha nova (13.18) exige também esta, além de `editar_empresa`. */
const PERMISSAO_DOS_GRUPOS = 'gerir_grupos_de_empresas'

/** O sufixo estável das linhas da ficha nova: `ficha-nova-<n>`. */
const PREFIXO_DA_LINHA = 'ficha-nova'

/** Enquanto a consulta ao ERP viaja (FR-47). */
const CONSULTANDO_O_ERP = 'Conferindo o CNPJ/CPF no Carga Máquina…'

/** Enquanto a BrasilAPI e o ViaCEP respondem (até 3 s, AD-28). */
export const CONSULTANDO_O_CNPJ = 'Consultando o CNPJ…'
export const CONSULTANDO_O_CEP = 'Consultando o CEP…'

/** O erro vermelho no campo (State Patterns: "BrasilAPI / ViaCEP fora"). */
export const CNPJ_NAO_ENCONTRADO = 'CNPJ não encontrado.'
export const CEP_NAO_ENCONTRADO = 'CEP não encontrado.'
export const NAO_FOI_POSSIVEL_CONSULTAR = 'Não foi possível consultar.'

/** Falha ao ler o vocabulário. Texto do SPA: é falha de carga, não mensagem de domínio. */
const LISTAS_INDISPONIVEIS =
  'Não foi possível carregar as listas de Categoria, Origem do cliente e Setor. Os seletores estão vazios por causa disso, e não porque as listas não existem — recarregue a página antes de escolher.'

/**
 * Falha ao ler o catálogo de campos personalizados — texto do SPA, como `LISTAS_INDISPONIVEIS`:
 * sem o aviso, a falta dos campos seria indistinguível de "a gestão não criou campo nenhum".
 */
export const CAMPOS_INDISPONIVEIS =
  'Não foi possível carregar os campos personalizados. Eles não aparecem por causa disso — os valores gravados continuam na ficha; recarregue a página para editá-los.'

/** O nome de um campo desativado que o serviço não soube dizer qual é. */
const CAMPO_DESATIVADO = 'Campo desativado'

/** Enquanto o `GET` da ficha viaja. */
const CARREGANDO_A_FICHA = 'Carregando a ficha…'

/** Enquanto o `GET` das listas viaja — vazio-porque-ainda-não-chegou não é instalação nova. */
const CARREGANDO_AS_LISTAS = 'Carregando as listas de Categoria, Origem do cliente e Setor…'

/** `existente` e `criada` terminam com uma ficha aberta; `inedito` segue no formulário. */
const ABREM_A_FICHA: readonly SituacaoDoDocumento[] = ['existente', 'criada']

const ID_DA_CONSULTA_NO_ERP = 'aviso-consulta-no-erp'
const ID_DA_RECUSA_DO_ERP = 'erro-consulta-no-erp'
const ID_DA_CONSULTA_DO_CNPJ = 'aviso-consulta-do-cnpj'
const ID_DA_RECUSA_DO_CNPJ = 'erro-consulta-do-cnpj'

/** O `id` do `<form>` — os campos e o botão dos cartões fora dele se associam por ele. */
export const ID_DO_FORMULARIO = 'formulario-da-empresa'

/** O rótulo que ocupa a primeira linha de todo seletor: "não escolhi nenhuma". */
const SEM_ESCOLHA = '— não informado —'

/** O que um seletor mostra quando a ficha aponta para uma opção que não está na lista. */
const OPCAO_FORA_DA_LISTA = 'valor atual (fora da lista)'

/** As três faixas do FR-81, na ordem da tela — os rótulos vêm de `faixa.ts`, a fonte única. */
const FAIXAS = Object.keys(ROTULOS_DA_FAIXA) as Faixa[]
const SEM_FAIXA = '— sem faixa —'
const SEM_UF = '— UF —'

/** As sete partes do endereço, na ordem da tela — o CEP primeiro, porque é ele que preenche. */
const PARTES_DO_ENDERECO = [
  'cep',
  'logradouro',
  'numero',
  'complemento',
  'bairro',
  'cidade',
  'uf',
] as const
type ParteDoEndereco = (typeof PARTES_DO_ENDERECO)[number]

/** Os rótulos do protótipo v2: "Rua" e "Estado", e não os nomes das colunas. */
const ROTULOS_DO_ENDERECO: Record<ParteDoEndereco, string> = {
  cep: 'CEP',
  logradouro: 'Rua',
  numero: 'Número',
  complemento: 'Complemento',
  bairro: 'Bairro',
  cidade: 'Cidade',
  uf: 'Estado',
}

/** As 27 unidades da federação, na ordem alfabética da sigla — o Estado é escolhido, não digitado. */
export const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA',
  'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
] as const

/** Os textos dos cartões, com as palavras do protótipo v2 (28/09/2026). */
export const TITULOS = {
  nova: 'Nova empresa',
  edicao: 'Editar empresa',
  contato: 'Informações para contato',
  endereco: 'Dados de endereço',
  pessoas: 'Pessoas',
  redes: 'Redes sociais',
} as const
const NOTA_DO_CNPJ = 'Comece pelo CNPJ: o sistema consulta a base pública e preenche o que encontrar.'
const NOTA_DO_CONTATO = 'Adicione informações que facilitem o contato com o cliente.'
const NOTA_DO_ENDERECO = 'Digite o CEP: rua, bairro, cidade e estado vêm sozinhos (ViaCEP).'
const NOTA_DAS_PESSOAS = 'Quem vocês falam nessa empresa. Cadastre aqui mesmo.'
const PLACEHOLDER_DO_DOCUMENTO = '00.000.000/0000-00 — digite e saia do campo para consultar'
/**
 * O que acontece ao sair do CNPJ **na criação**, escrito uma vez, abaixo do campo — e só o que
 * acontece de verdade: o Carga Máquina confere se a empresa já está lá, a BrasilAPI traz razão
 * social, nome fantasia (13.7) e endereço (não o telefone, que o serviço não repassa). A checagem de
 * duplicidade cobre só o Carga Máquina. A última frase é a do Gustavo (22/10/2026), literal.
 */
export const AJUDA_DO_DOCUMENTO =
  'Ao sair do campo, o CRM confere no Carga Máquina se a empresa já está cadastrada lá e, se for nova, consulta a base pública (BrasilAPI/Receita): razão social, nome fantasia e endereço. A checagem de duplicidade cobre só o Carga Máquina — empresas repetidas na base do CRM continuam possíveis, e a limpeza é da equipe. Sem CNPJ, a empresa não é cadastrada no Carga Máquina.'

/**
 * A mesma ajuda **na edição** (13.11c): a ficha já existe, e o `blur` não consulta o Carga Máquina —
 * só a BrasilAPI, quando o CNPJ mudou (`conferirNoErp`). Prometer a consulta ao Carga Máquina aqui
 * seria descrever o que não acontece.
 */
export const AJUDA_DO_DOCUMENTO_NA_EDICAO =
  'Ao sair do campo com um CNPJ novo, o CRM consulta a base pública (BrasilAPI/Receita) e preenche razão social, nome fantasia e endereço. Sem CNPJ, a empresa não é cadastrada no Carga Máquina.'

/** O Responsável escolhido no Negócio cujo nome a equipe ainda não trouxe (13.1). */
const RESPONSAVEL_SEM_NOME = 'Responsável escolhido no Negócio'

/**
 * O aviso fixo da ficha **no modal** do Novo negócio, com o documento vazio (decisão P-C de
 * 21/10/2026): a sugestão de Empresas é recortada pela RLS (FR-30, "é responsável, vê"), e a Empresa
 * de outro responsável pode não aparecer nela — quem a procura e não acha precisa saber que o CNPJ é
 * o caminho. "Pode": quem é Responsável de um Negócio dela (PA-1), tem Tarefa aberta (PA-3) ou a vê
 * pelo Grupo a encontra.
 */
export const AVISO_DE_EMPRESA_DE_OUTRO_RESPONSAVEL =
  'Empresas de outro responsável podem não aparecer aqui; informe o CNPJ para o CRM conferir no Carga Máquina.'
/**
 * O CNPJ/CPF somente leitura na edição (13.14a, DW-509 e DW-507) — conveniência, e não autorização:
 * quem recusa é o serviço (o `409`) e o gatilho do banco.
 */
export const DOCUMENTO_SO_DO_RESPONSAVEL =
  'Só o Responsável pela empresa ou a gestão alteram o CNPJ/CPF.'
export const DOCUMENTO_DA_FICHA_ENVIADA =
  'Esta empresa já foi enviada ao Carga Máquina: só a gestão troca o CNPJ/CPF. Se ele está errado, peça à gestão.'
/** 13.14b (DW-520): a ficha casada no Carga Máquina não muda o CNPJ/CPF por perfil nenhum. */
export const DOCUMENTO_DA_FICHA_CASADA =
  'Esta empresa já está cadastrada no Carga Máquina: o CNPJ/CPF só se corrige lá, pela operação.'
const ID_DO_BLOQUEIO_DO_DOCUMENTO = 'bloqueio-do-documento'

/**
 * Por que o CNPJ/CPF da ficha **em edição** não se edita, ou `null` quando se edita (13.14a).
 *
 * A ordem é a do serviço (o `409` de `editar_empresa`):
 *
 * - **sem a sessão ainda**: somente leitura, sem texto — não se sabe quem é;
 * - **quem não é o dono** (edita pela PA-1 ou pelo Grupo, DW-509; `quem_le_e_o_dono` da resposta
 *   desde a 13.14b): `DOCUMENTO_SO_DO_RESPONSAVEL`;
 * - **a ficha casada no Carga Máquina** (`erp_pessoa_id`, 13.14b, DW-520): `DOCUMENTO_DA_FICHA_CASADA`
 *   — para a gestão também, e mesmo para preencher o vazio;
 * - **a gestão** (escopo `todas`): editável;
 * - **o Responsável, na ficha com documento e liberada ao Carga Máquina** (a ida já pedida, DW-507):
 *   `DOCUMENTO_DA_FICHA_ENVIADA`. Preencher o que falta continua dele.
 */
export function bloqueioDoDocumento(
  empresa: Pick<
    Empresa,
    'documento' | 'erp_pessoa_id' | 'liberada_para_o_erp' | 'quem_le_e_o_dono'
  >,
  sessao: { escopo_de_carteira?: string | null } | undefined,
): string | null {
  if (sessao === undefined) {
    return ''
  }
  if (!empresa.quem_le_e_o_dono) {
    return DOCUMENTO_SO_DO_RESPONSAVEL
  }
  if (empresa.erp_pessoa_id !== null) {
    return DOCUMENTO_DA_FICHA_CASADA
  }
  if (sessao.escopo_de_carteira === 'todas') {
    return null
  }
  return empresa.documento !== null && empresa.liberada_para_o_erp
    ? DOCUMENTO_DA_FICHA_ENVIADA
    : null
}

const ADICIONAR_ENTREGA = '+ Adicionar endereço de entrega'
const PODE_TER_MAIS_DE_UM = 'Pode ter mais de um.'
const ID_DA_AJUDA_DO_DOCUMENTO = 'ajuda-do-documento'

type EnderecoNoRascunho = Record<ParteDoEndereco, string>

const ENDERECO_VAZIO: EnderecoNoRascunho = {
  cep: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: '',
}

/** Só dígitos: é o que o serviço guarda, e o que o evento carrega. */
function somenteDigitos(texto: string): string {
  return texto.replace(/\D/g, '')
}

/** `01001000` → `01001-000`: o CEP como a pessoa o lê. */
function cepNaTela(cep: string | null | undefined): string {
  if (!cep) {
    return ''
  }
  return cep.length === DIGITOS_DO_CEP ? `${cep.slice(0, 5)}-${cep.slice(5)}` : cep
}

/** O estado do formulário: todo campo é texto, porque todo campo é um controle controlado. */
interface Rascunho {
  documento: string
  razao_social: string
  /** O nome fantasia (13.7) — o nome exibido da Empresa em toda tela. */
  nome_fantasia: string
  categoria_id: string
  setor_id: string
  origem_id: string
  faixa: Faixa | ''
  descricao: string
  telefone: string
  email: string
  site: string
  instagram: string
  linkedin: string
  facebook: string
  endereco: EnderecoNoRascunho
  /** `definicao_id -> valor` como o controle o guarda (sempre texto). */
  campos: Record<string, string>
}

const RASCUNHO_VAZIO: Rascunho = {
  documento: '',
  razao_social: '',
  nome_fantasia: '',
  categoria_id: '',
  setor_id: '',
  origem_id: '',
  faixa: '',
  descricao: '',
  telefone: '',
  email: '',
  site: '',
  instagram: '',
  linkedin: '',
  facebook: '',
  endereco: ENDERECO_VAZIO,
  campos: {},
}

/**
 * O valor gravado como o controle o mostra: número com **vírgula decimal** (pt-BR, NFR-8), texto
 * como veio.
 */
function valorNoControle(valor: string | number | null | undefined): string {
  if (valor === undefined || valor === null) {
    return ''
  }
  return typeof valor === 'number' ? String(valor).replace('.', ',') : valor
}

/**
 * O número digitado num campo personalizado, ou `null` quando ele não é um número finito.
 *
 * Aceita a vírgula decimal do pt-BR ("120,5") e o ponto ("120.5"); com vírgula, pontos são de
 * milhar ("1.234,5"). Só dígitos ASCII, sem expoente: "1e999" viraria `Infinity`, e `NaN` e
 * `Infinity` iriam ao JSON como `null` — o campo seria **apagado** por acidente.
 */
export function numeroDoCampo(texto: string): number | null {
  const aparado = texto.trim()
  const semMilhar = aparado.includes(',') ? aparado.replace(/\./g, '').replace(',', '.') : aparado
  if (!/^-?[0-9]+(\.[0-9]+)?$/.test(semMilhar)) {
    return null
  }
  const numero = Number(semMilhar)
  return Number.isFinite(numero) ? numero : null
}

/** A recusa local de um número que não é número — nomeando o campo, e com o exemplo pt-BR. */
export function numeroInvalido(nome: string): string {
  return `Informe um número no campo ${nome}, como 120,5.`
}

function rascunhoDa(empresa: Empresa): Rascunho {
  return {
    // `?? ''` porque a ficha nascida pela porta enxuta (FR-3) ainda não tem CNPJ: o campo é um
    // `input` controlado, e `null` nele o tornaria não-controlado no meio da digitação.
    documento: empresa.documento ?? '',
    razao_social: empresa.razao_social,
    nome_fantasia: empresa.nome_fantasia ?? '',
    categoria_id: empresa.categoria_id ?? '',
    setor_id: empresa.setor_id ?? '',
    origem_id: empresa.origem_id ?? '',
    faixa: empresa.faixa ?? '',
    descricao: empresa.descricao ?? '',
    telefone: empresa.telefone ?? '',
    email: empresa.email ?? '',
    site: empresa.site ?? '',
    instagram: empresa.instagram ?? '',
    linkedin: empresa.linkedin ?? '',
    facebook: empresa.facebook ?? '',
    endereco: {
      cep: cepNaTela(empresa.endereco.cep),
      logradouro: empresa.endereco.logradouro ?? '',
      numero: empresa.endereco.numero ?? '',
      complemento: empresa.endereco.complemento ?? '',
      bairro: empresa.endereco.bairro ?? '',
      cidade: empresa.endereco.cidade ?? '',
      uf: empresa.endereco.uf ?? '',
    },
    campos: Object.fromEntries(
      Object.entries(empresa.campos_personalizados).map(([id, valor]) => [
        id,
        valorNoControle(valor),
      ]),
    ),
  }
}

/** Campo opcional vazio vai como `null`, e não como `""`: "apagado" é um estado só. */
function ouNulo(valor: string): string | null {
  const aparado = valor.trim()
  return aparado === '' ? null : aparado
}

function corpoDoEndereco(endereco: EnderecoNoRascunho): CorpoDeEndereco {
  return {
    logradouro: ouNulo(endereco.logradouro),
    numero: ouNulo(endereco.numero),
    complemento: ouNulo(endereco.complemento),
    bairro: ouNulo(endereco.bairro),
    cidade: ouNulo(endereco.cidade),
    uf: ouNulo(endereco.uf),
    cep: ouNulo(endereco.cep),
  }
}

/** Uma definição de campo personalizado de Empresa (o catálogo da 11.3). */
type DefinicaoDeCampo = Awaited<ReturnType<typeof camposDaEmpresa.lerCampos>>[number]

/**
 * O **delta** dos campos personalizados (FR-79): só o que mudou em relação à ficha gravada — o
 * vazio vira `null` (apaga), o número vai como número. Ausente, o serviço não mexe.
 *
 * Número que não é número **não entra** aqui: `recusaDosCampos` o recusa antes do envio. Sem a
 * recusa, `Number('abc')` seria `NaN`, e `NaN` vai ao JSON como `null` — apagaria o valor gravado
 * sem ninguém ter pedido.
 */
function deltaDosCampos(
  rascunho: Rascunho,
  gravados: Record<string, string>,
  definicoes: readonly DefinicaoDeCampo[],
): Record<string, string | number | null> | undefined {
  const delta: Record<string, string | number | null> = {}
  for (const definicao of definicoes) {
    const novo = (rascunho.campos[definicao.id] ?? '').trim()
    const atual = (gravados[definicao.id] ?? '').trim()
    if (definicao.tipo === 'numero') {
      const numeroNovo = novo === '' ? null : numeroDoCampo(novo)
      const numeroAtual = atual === '' ? null : numeroDoCampo(atual)
      if (numeroNovo === numeroAtual || (novo !== '' && numeroNovo === null)) {
        continue
      }
      delta[definicao.id] = numeroNovo
      continue
    }
    if (novo === atual) {
      continue
    }
    delta[definicao.id] = novo === '' ? null : novo
  }
  return Object.keys(delta).length === 0 ? undefined : delta
}

/** A primeira recusa local dos campos personalizados (número que não é número), ou `null`. */
function recusaDosCampos(
  rascunho: Rascunho,
  definicoes: readonly DefinicaoDeCampo[],
): string | null {
  for (const definicao of definicoes) {
    const valor = (rascunho.campos[definicao.id] ?? '').trim()
    if (definicao.tipo === 'numero' && valor !== '' && numeroDoCampo(valor) === null) {
      return numeroInvalido(definicao.nome)
    }
  }
  return null
}

/**
 * O telefone do corpo: o que **difere do gravado** passa por `telefoneParaGravar` (13.2); o que a
 * pessoa não tocou volta como está — o `PUT` é a ficha inteira, e uma grafia antiga não muda por
 * uma edição de outro campo.
 */
function telefoneDoCorpo(digitado: string, gravado: string | null): string | null {
  return ouNulo(digitado === (gravado ?? '') ? digitado : telefoneParaGravar(digitado))
}

/**
 * O nome fantasia do corpo (13.7): na **criação** (`gravado === undefined`) sempre; na **edição**, só
 * quando a pessoa o mudou em relação ao que a ficha tinha ao abrir. Fora disso a chave sai do corpo, e
 * o servidor **mantém** o que está gravado — a adoção pelo ERP pode ter preenchido o nome fantasia
 * entre dois salvamentos desta ficha aberta, e mandar o `""` do rascunho o apagaria.
 */
function nomeFantasiaDoCorpo(
  digitado: string,
  gravado: string | null | undefined,
): { nome_fantasia?: string | null } {
  const novo = ouNulo(digitado)
  if (gravado !== undefined && novo === ouNulo(gravado ?? '')) {
    return {}
  }
  return { nome_fantasia: novo }
}

function corpoDa(
  rascunho: Rascunho,
  camposGravados: Record<string, string>,
  definicoes: readonly DefinicaoDeCampo[],
  telefoneGravado: string | null,
  nomeFantasiaGravado: string | null | undefined,
): EdicaoDeEmpresa {
  const campos = deltaDosCampos(rascunho, camposGravados, definicoes)
  const documento = somenteDigitos(rascunho.documento)
  return {
    // Vazio é "sem CNPJ/CPF" (13.1): `null`, e não `""` — um estado só para a mesma intenção.
    documento: documento === '' ? null : documento,
    razao_social: rascunho.razao_social.trim(),
    ...nomeFantasiaDoCorpo(rascunho.nome_fantasia, nomeFantasiaGravado),
    categoria_id: ouNulo(rascunho.categoria_id),
    setor_id: ouNulo(rascunho.setor_id),
    origem_id: ouNulo(rascunho.origem_id),
    faixa: rascunho.faixa === '' ? null : rascunho.faixa,
    descricao: ouNulo(rascunho.descricao),
    telefone: telefoneDoCorpo(rascunho.telefone, telefoneGravado),
    email: ouNulo(rascunho.email),
    site: ouNulo(rascunho.site),
    instagram: ouNulo(rascunho.instagram),
    linkedin: ouNulo(rascunho.linkedin),
    facebook: ouNulo(rascunho.facebook),
    endereco: corpoDoEndereco(rascunho.endereco),
    ...(campos === undefined ? {} : { campos_personalizados: campos }),
  }
}

/**
 * A recusa local, ou `null` quando não há nenhuma. O servidor continua sendo quem decide.
 *
 * O documento vazio **não** é recusado desde a 13.1 (FR-24 emendado); preenchido, a contagem de
 * dígitos continua conferida aqui.
 */
function recusaLocal(rascunho: Rascunho): string | null {
  const digitos = somenteDigitos(rascunho.documento)
  if (digitos !== '' && digitos.length !== DIGITOS_DO_CPF && digitos.length !== DIGITOS_DO_CNPJ) {
    return DOCUMENTO_INVALIDO
  }
  if (rascunho.razao_social.trim() === '') {
    return FALTA_RAZAO_SOCIAL
  }
  return null
}

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/** O erro vermelho de uma consulta pública que não trouxe dado. */
function erroDaConsulta(motivo: string | null | undefined, naoEncontrado: string): string {
  return motivo === 'nao_encontrado' ? naoEncontrado : NAO_FOI_POSSIVEL_CONSULTAR
}

function preenchimentoDoCnpj(consulta: ConsultaDeCnpj): Trazido {
  const trazido: Trazido = {}
  for (const parte of PARTES_DO_ENDERECO) {
    const valor = consulta.endereco[parte]
    if (valor) {
      trazido[parte] = parte === 'cep' ? cepNaTela(valor) : valor
    }
  }
  if (consulta.razao_social) {
    trazido.razao_social = consulta.razao_social
  }
  // 13.7 (D-8): o nome fantasia que conta e que não é a própria razão social com outra pontuação.
  const nomeFantasia = nomeFantasiaQueConta(consulta.nome_fantasia)
  if (
    nomeFantasia !== null &&
    (!consulta.razao_social ||
      normalizarParaComparar(nomeFantasia) !== normalizarParaComparar(consulta.razao_social))
  ) {
    trazido.nome_fantasia = nomeFantasia
  }
  return trazido
}

function preenchimentoDoCep(consulta: ConsultaDeCep): Partial<EnderecoNoRascunho> {
  const endereco: Partial<EnderecoNoRascunho> = {}
  // O CEP não é sobrescrito pela própria consulta; `numero` nunca vem de CEP.
  for (const parte of ['logradouro', 'complemento', 'bairro', 'cidade', 'uf'] as const) {
    const valor = consulta.endereco[parte]
    if (valor) {
      endereco[parte] = valor
    }
  }
  return endereco
}

/**
 * Os campos que uma consulta pública pode preencher: a razão social, o nome fantasia (13.7) e as
 * partes do endereço.
 */
type CampoConsultavel = 'razao_social' | 'nome_fantasia' | ParteDoEndereco

/** O que uma consulta trouxe, campo a campo — só o que tem valor. */
type Trazido = Partial<Record<CampoConsultavel, string>>

const ROTULOS_DOS_CONSULTAVEIS: Record<CampoConsultavel, string> = {
  razao_social: 'Razão social',
  nome_fantasia: 'Nome fantasia',
  ...ROTULOS_DO_ENDERECO,
}

/**
 * O valor como se compara: CEP só com dígitos, UF maiúscula, o resto aparado. `01001-000` e
 * `01001000` são o mesmo CEP, e `sp` e `SP` a mesma UF — perguntar "substituir?" entre eles seria
 * perguntar à toa.
 */
function comparavel(campo: CampoConsultavel, valor: string): string {
  const aparado = valor.trim()
  if (campo === 'cep') {
    return somenteDigitos(aparado)
  }
  // 13.7: "vale verde" e "VALE VERDE" são o mesmo nome fantasia — perguntar entre eles seria à toa.
  if (campo === 'nome_fantasia') {
    return aparado.toLocaleLowerCase('pt-BR')
  }
  return campo === 'uf' ? aparado.toUpperCase() : aparado
}

function valorNoRascunho(rascunho: Rascunho, campo: CampoConsultavel): string {
  if (campo === 'razao_social' || campo === 'nome_fantasia') {
    return rascunho[campo]
  }
  return rascunho.endereco[campo]
}

function comOValor(rascunho: Rascunho, campo: CampoConsultavel, valor: string): Rascunho {
  if (campo === 'razao_social' || campo === 'nome_fantasia') {
    return { ...rascunho, [campo]: valor }
  }
  return { ...rascunho, endereco: { ...rascunho.endereco, [campo]: valor } }
}

/**
 * A regra de preenchimento (FR-24), contra o rascunho **de agora**: o vazio é preenchido; o igual
 * (comparado normalizado) fica como está; o diferente volta como conflito — a pergunta.
 */
function mesclar(rascunho: Rascunho, trazido: Trazido): { rascunho: Rascunho; conflitos: Trazido } {
  let novo = rascunho
  const emConflito: Trazido = {}
  for (const [campo, valor] of Object.entries(trazido) as [CampoConsultavel, string][]) {
    const agora = valorNoRascunho(novo, campo)
    if (agora.trim() === '') {
      novo = comOValor(novo, campo, valor)
    } else if (comparavel(campo, agora) !== comparavel(campo, valor)) {
      emConflito[campo] = valor
    }
  }
  return { rascunho: novo, conflitos: emConflito }
}

/** Aplica o que veio: sempre nos vazios; nos preenchidos só quando `sobrescrever`. */
function aplicarNoEndereco(
  atual: EnderecoNoRascunho,
  trazido: Partial<EnderecoNoRascunho>,
  sobrescrever: boolean,
): EnderecoNoRascunho {
  const novo = { ...atual }
  for (const [parte, valor] of Object.entries(trazido) as [ParteDoEndereco, string][]) {
    if (sobrescrever || novo[parte].trim() === '') {
      novo[parte] = valor
    }
  }
  return novo
}

function Seletor({
  id,
  rotulo,
  valor,
  opcoes,
  rotulosForaDaLista,
  aoTrocar,
}: {
  id: string
  rotulo: string
  valor: string
  opcoes: readonly OpcaoDeDominio[]
  /** `id -> rótulo` dos valores que a ficha usa e que saíram do vocabulário ativo. */
  rotulosForaDaLista: ReadonlyMap<string, string>
  aoTrocar: (valor: string) => void
}) {
  /*
   * A ficha pode apontar para uma opção que não está na lista carregada (excluída logicamente, ou
   * a leitura das listas falhou). Sem esta linha o `<select>` mostraria "não informado" e
   * continuaria submetendo o id antigo: a tela mostraria uma coisa e gravaria outra. O rótulo é o
   * de verdade (FR-67), e `OPCAO_FORA_DA_LISTA` fica como último recurso.
   */
  const conhecida = valor === '' || opcoes.some((opcao) => opcao.id === valor)
  const rotuloDoValorAtual = rotulosForaDaLista.get(valor) ?? OPCAO_FORA_DA_LISTA

  return (
    <div className="empresa__campo">
      <label className="empresa__rotulo" htmlFor={id}>
        {rotulo}
      </label>
      <select
        id={id}
        className="empresa__selecao"
        value={valor}
        onChange={(evento) => {
          aoTrocar(evento.target.value)
        }}
      >
        {/* Lista vazia rende só esta linha — seletor vazio, nunca erro (FR-67). */}
        <option value="">{SEM_ESCOLHA}</option>
        {conhecida ? null : <option value={valor}>{rotuloDoValorAtual}</option>}
        {opcoes.map((opcao) => (
          <option key={opcao.id} value={opcao.id}>
            {opcao.rotulo}
          </option>
        ))}
      </select>
    </div>
  )
}

function Texto({
  id,
  rotulo,
  valor,
  aoTrocar,
  aoSairDoCampo,
  aoTeclar,
  tipo = 'text',
  className = 'empresa__entrada',
  largo = false,
  autoFocus = false,
  inputMode,
  maxLength,
  descritoPor,
  invalido = false,
  formulario,
  placeholder,
  autoComplete,
  somenteLeitura = false,
  obrigatorio = false,
}: {
  id: string
  rotulo: string
  valor: string
  aoTrocar: (valor: string) => void
  placeholder?: string
  /** Chamado no `blur` — o documento e o CEP disparam as consultas por ele. */
  aoSairDoCampo?: () => void
  aoTeclar?: (evento: KeyboardEvent<HTMLInputElement>) => void
  /** `tel` liga a máscara de telefone ao digitar e o `telefoneParaGravar` no `blur` (13.2). */
  tipo?: 'text' | 'tel' | 'email'
  className?: string
  largo?: boolean
  autoFocus?: boolean
  inputMode?: 'text' | 'numeric' | 'tel' | 'email' | 'url' | 'decimal'
  maxLength?: number
  /** `id` do aviso que descreve **este** campo, quando há um. */
  descritoPor?: string
  /** O erro vermelho no campo (a consulta pública não trouxe dado). */
  invalido?: boolean
  /** O `id` do `<form>` quando o campo mora num cartão fora dele (Redes sociais). */
  formulario?: string
  /** O `autoComplete` com a seção (`section-empresa work tel`, DW-92 e D-12 da 13.2). */
  autoComplete?: string
  /** O CNPJ/CPF que quem edita não pode trocar (13.14a): visível, focável, sem digitação. */
  somenteLeitura?: boolean
  /** O `*` vermelho no rótulo e o `aria-required` no campo (13.15). */
  obrigatorio?: boolean
}) {
  /* O valor quando o campo ganhou o foco: o `blur` só normaliza o telefone que mudou (13.2). */
  const valorNoFoco = useRef(valor)
  const ehTelefone = tipo === 'tel'
  return (
    <div className={largo ? 'empresa__campo empresa__campo--largo' : 'empresa__campo'}>
      <LinhaDoRotulo obrigatorio={obrigatorio}>
        <label className="empresa__rotulo" htmlFor={id}>
          {rotulo}
        </label>
      </LinhaDoRotulo>
      <input
        id={id}
        className={className}
        type={tipo}
        value={valor}
        autoFocus={autoFocus}
        inputMode={inputMode}
        maxLength={maxLength}
        autoComplete={autoComplete}
        aria-describedby={descritoPor}
        aria-invalid={invalido ? true : undefined}
        aria-required={obrigatorio ? true : undefined}
        form={formulario}
        placeholder={placeholder}
        readOnly={somenteLeitura}
        onFocus={(evento) => {
          valorNoFoco.current = evento.target.value
        }}
        onBlur={(evento) => {
          const atual = evento.target.value
          if (ehTelefone && atual !== valorNoFoco.current) {
            const gravar = telefoneParaGravar(atual)
            if (gravar !== atual) {
              aoTrocar(gravar)
            }
          }
          aoSairDoCampo?.()
        }}
        onKeyDown={aoTeclar}
        onChange={(evento) => {
          aoTrocar(ehTelefone ? aoDigitarTelefone(evento, valor) : evento.target.value)
        }}
      />
    </div>
  )
}

/**
 * O Estado (UF) como escolha, e não digitação (protótipo v2). O valor gravado que não é uma das
 * 27 siglas continua visível e submetível — como o `Seletor` das listas faz com a opção fora da
 * lista —, para a tela não mostrar uma coisa e gravar outra.
 */
function SeletorDeUf({
  id,
  valor,
  aoTrocar,
}: {
  id: string
  valor: string
  aoTrocar: (valor: string) => void
}) {
  const conhecida = valor === '' || (UFS as readonly string[]).includes(valor)
  return (
    <div className="empresa__campo">
      <label className="empresa__rotulo" htmlFor={id}>
        {ROTULOS_DO_ENDERECO.uf}
      </label>
      <select
        id={id}
        className="empresa__selecao"
        value={valor}
        onChange={(evento) => {
          aoTrocar(evento.target.value)
        }}
      >
        {/* Curto, porque a caixa tem 110px: "— não informado —" não caberia. */}
        <option value="">{SEM_UF}</option>
        {conhecida ? null : <option value={valor}>{valor}</option>}
        {UFS.map((uf) => (
          <option key={uf} value={uf}>
            {uf}
          </option>
        ))}
      </select>
    </div>
  )
}

/** Um cartão da ficha: título 750, uma nota apagada, e o miolo. Nomeia a `<section>`. */
function Cartao({
  id,
  titulo,
  ajuda,
  children,
}: {
  id: string
  titulo: string
  ajuda?: string
  children: ReactNode
}) {
  return (
    <section className="empresa__cartao" aria-labelledby={id}>
      <div className="empresa__cabecalho">
        <h2 className="empresa__titulo" id={id}>
          {titulo}
        </h2>
        {ajuda === undefined ? null : <span className="empresa__ajuda">{ajuda}</span>}
      </div>
      <div className="empresa__corpo">{children}</div>
    </section>
  )
}

/**
 * O campo Responsável — **só no modo de criação**, somente leitura: é quem está cadastrando
 * (FR-33). Na edição quem ocupa o mesmo lugar da grade é o campo `TrocaDeResponsavel`, passado
 * por quem monta a tela — duas cópias do mesmo valor na mesma tela divergiriam na primeira
 * gravação.
 */
function Responsavel({ nomeDeQuemEstaLogado }: { nomeDeQuemEstaLogado: string }) {
  return (
    <div className="empresa__campo">
      <label className="empresa__rotulo" htmlFor="campo-responsavel">
        Responsável
      </label>
      <input
        id="campo-responsavel"
        className="empresa__entrada"
        type="text"
        readOnly
        value={nomeDeQuemEstaLogado}
      />
    </div>
  )
}

/**
 * O Responsável **escolhível** da criação no modal do Novo negócio (13.1): começa no Responsável
 * escolhido no Negócio e pode ser trocado — é o que a porta enxuta fazia (ela gravava o
 * Responsável do Negócio como o da Empresa). Só a equipe ativa é escolha; o valor que a lista
 * ainda não conhece (a equipe não chegou) continua visível, para o envio não sair no nome de outra
 * pessoa calado.
 */
function ResponsavelEscolhivel({
  valor,
  idDeQuemEstaLogado,
  nomeDeQuemEstaLogado,
  aoTrocar,
}: {
  valor: string
  idDeQuemEstaLogado: string
  nomeDeQuemEstaLogado: string
  aoTrocar: (valor: string) => void
}) {
  const equipe = useResponsaveis()
  const escolhiveis = (equipe.data ?? []).filter((pessoa) => pessoa.ativo)
  const conhecido = escolhiveis.some((pessoa) => pessoa.id === valor)
  /*
   * O rótulo do valor que a lista ativa não tem: o nome **da equipe** para aquele id (inclusive
   * desativado), e o de quem entrou **só** quando o id é o dele. Nunca o nome de uma pessoa sobre
   * o id de outra — a tela diria um Responsável e gravaria outro.
   */
  const rotuloDoValor =
    (equipe.data ?? []).find((pessoa) => pessoa.id === valor)?.nome ??
    (valor === idDeQuemEstaLogado && nomeDeQuemEstaLogado !== ''
      ? nomeDeQuemEstaLogado
      : RESPONSAVEL_SEM_NOME)
  return (
    <div className="empresa__campo">
      {/* 13.15: o seletor é obrigatório — a ficha não nasce sem Responsável. */}
      <LinhaDoRotulo obrigatorio>
        <label className="empresa__rotulo" htmlFor="campo-responsavel">
          Responsável
        </label>
      </LinhaDoRotulo>
      <select
        id="campo-responsavel"
        className="empresa__selecao"
        aria-required
        value={valor}
        onChange={(evento) => {
          aoTrocar(evento.target.value)
        }}
      >
        {conhecido ? null : <option value={valor}>{rotuloDoValor}</option>}
        {escolhiveis.map((pessoa) => (
          <option key={pessoa.id} value={pessoa.id}>
            {pessoa.nome}
          </option>
        ))}
      </select>
    </div>
  )
}

/** Um campo personalizado (FR-79) em Dados básicos, conforme o tipo da definição. */
function CampoPersonalizado({
  definicao,
  valor,
  aoTrocar,
}: {
  definicao: DefinicaoDeCampo
  valor: string
  aoTrocar: (valor: string) => void
}) {
  const id = `campo-personalizado-${definicao.id}`
  return (
    <div className="empresa__campo">
      {/* "NOME · personalizado" (protótipo v2): o sufixo fica **fora** do `<label>`, em peso
          normal e sem versalete — e o nome acessível do campo continua sendo só o nome. */}
      <div className="empresa__rotulo-linha">
        <label className="empresa__rotulo" htmlFor={id}>
          {definicao.nome}
        </label>
        <span className="empresa__rotulo-sufixo" aria-hidden="true">
          · personalizado
        </span>
      </div>
      {definicao.tipo === 'lista' ? (
        <select
          id={id}
          className="empresa__selecao"
          value={valor}
          onChange={(evento) => {
            aoTrocar(evento.target.value)
          }}
        >
          <option value="">{SEM_ESCOLHA}</option>
          {/* O valor gravado que saiu das opções continua visível (FR-67). */}
          {valor === '' || definicao.opcoes.includes(valor) ? null : (
            <option value={valor}>{valor}</option>
          )}
          {definicao.opcoes.map((opcao) => (
            <option key={opcao} value={opcao}>
              {opcao}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={id}
          className={definicao.tipo === 'numero' ? 'empresa__entrada numero' : 'empresa__entrada'}
          /* Número é `text` com teclado decimal, e não `type="number"`: este não aceita a vírgula
             do pt-BR ("120,5") em todo navegador, e entregaria `""` no lugar do que foi digitado. */
          type={definicao.tipo === 'data' ? 'date' : 'text'}
          inputMode={definicao.tipo === 'numero' ? 'decimal' : undefined}
          value={valor}
          onChange={(evento) => {
            aoTrocar(evento.target.value)
          }}
        />
      )}
    </div>
  )
}

/**
 * A pergunta antes de sobrescrever o que a pessoa já digitou (FR-24: "o que já estava digitado
 * não é sobrescrito sem confirmação"). Os vazios já foram preenchidos; aqui só os conflitantes.
 */
function Confirmacao({
  campos,
  aoSubstituir,
  aoManter,
}: {
  campos: readonly string[]
  aoSubstituir: () => void
  aoManter: () => void
}) {
  return (
    <div className="empresa__confirmacao" role="group" aria-label="Substituir o que foi digitado">
      <p className="empresa__confirmacao-texto">
        {`A consulta trouxe outro valor para ${campos.join(', ')}. Substituir o que foi digitado?`}
      </p>
      <div className="empresa__acoes">
        <button type="button" className="empresa__botao" onClick={aoSubstituir}>
          Substituir
        </button>
        <button type="button" className="empresa__botao empresa__botao--discreto" onClick={aoManter}>
          Manter o que digitei
        </button>
      </div>
    </div>
  )
}

/**
 * O CEP com a consulta ao ViaCEP ao sair do campo — o do principal e o de cada entrega.
 *
 * **O `ref` acompanha o valor atual do campo**, e não só o que foi digitado: o CEP também muda
 * quando a consulta do CNPJ o preenche ou quando a pessoa aceita "Substituir". Ele é atualizado
 * num efeito (escrever em `ref` durante a renderização é o que `react-hooks/refs` proíbe) e na
 * própria digitação; é por ele que a resposta atrasada — de um CEP que já não está no campo — é
 * descartada.
 *
 * **Não encontrado conta como consultado; indisponível, não.** O mesmo CEP inexistente não vai
 * ao ViaCEP de novo a cada saída do campo, mas o provedor fora (ou a queda do próprio CRM) deixa
 * tentar outra vez: sair e voltar ao campo é o "tentar de novo" dele.
 */
function useConsultaDeCep(
  valorAtual: string,
  aoTrazer: (endereco: Partial<EnderecoNoRascunho>) => void,
) {
  const consulta = useConsultarCep()
  const [erro, setErro] = useState<string | null>(null)
  const [consultado, setConsultado] = useState<string | null>(null)
  const cepNoCampo = useRef(valorAtual)
  useEffect(() => {
    cepNoCampo.current = valorAtual
  }, [valorAtual])

  function acompanhar(valor: string) {
    cepNoCampo.current = valor
    setErro(null)
  }

  function consultar() {
    const digitos = somenteDigitos(cepNoCampo.current)
    if (digitos.length !== DIGITOS_DO_CEP || digitos === consultado) {
      return
    }
    setConsultado(digitos)
    consulta.mutate(digitos, {
      onSuccess: (resposta) => {
        if (somenteDigitos(cepNoCampo.current) !== digitos) {
          return
        }
        if (!resposta.encontrado) {
          if (resposta.motivo !== 'nao_encontrado') {
            setConsultado(null)
          }
          setErro(erroDaConsulta(resposta.motivo, CEP_NAO_ENCONTRADO))
          return
        }
        setErro(null)
        aoTrazer(preenchimentoDoCep(resposta))
      },
      onError: () => {
        if (somenteDigitos(cepNoCampo.current) !== digitos) {
          return
        }
        // Falha de rede do próprio CRM também é "não foi possível" — e deixa tentar de novo.
        setConsultado(null)
        setErro(NAO_FOI_POSSIVEL_CONSULTAR)
      },
    })
  }

  return { consultando: consulta.isPending, erro, acompanhar, consultar }
}

/** As sete partes do endereço, com o CEP consultado no ViaCEP. */
function CamposDoEndereco({
  prefixo,
  endereco,
  aoTrocar,
  aoTrazerDoCep,
  aoTeclar,
}: {
  prefixo: string
  endereco: EnderecoNoRascunho
  aoTrocar: (parte: ParteDoEndereco, valor: string) => void
  aoTrazerDoCep: (trazido: Partial<EnderecoNoRascunho>) => void
  aoTeclar?: (evento: KeyboardEvent<HTMLInputElement>) => void
}) {
  const cep = useConsultaDeCep(endereco.cep, aoTrazerDoCep)
  const idDoAviso = `${prefixo}-aviso-cep`
  const idDoErro = `${prefixo}-erro-cep`
  const texto = (parte: Exclude<ParteDoEndereco, 'uf'>, placeholder?: string) => (
    <Texto
      id={`${prefixo}-${parte}`}
      rotulo={ROTULOS_DO_ENDERECO[parte]}
      valor={endereco[parte]}
      maxLength={TAMANHOS_DO_ENDERECO[parte]}
      inputMode={parte === 'cep' ? 'numeric' : undefined}
      className={parte === 'cep' ? 'empresa__entrada numero' : 'empresa__entrada'}
      placeholder={placeholder}
      aoTeclar={aoTeclar}
      aoTrocar={(valor) => {
        if (parte === 'cep') {
          cep.acompanhar(valor)
        }
        aoTrocar(parte, valor)
      }}
      aoSairDoCampo={parte === 'cep' ? cep.consultar : undefined}
      descritoPor={
        parte !== 'cep'
          ? undefined
          : cep.erro !== null
            ? idDoErro
            : cep.consultando
              ? idDoAviso
              : undefined
      }
      invalido={parte === 'cep' && cep.erro !== null}
    />
  )
  /* As três linhas do protótipo v2: CEP | Estado · Cidade | Bairro · Rua | Número | Complemento.
     O CEP vem primeiro porque é ele que preenche o resto. */
  return (
    <div className="empresa__endereco">
      <div className="empresa__grade empresa__grade--endereco">
        {texto('cep', '00000-000')}
        <SeletorDeUf
          id={`${prefixo}-uf`}
          valor={endereco.uf}
          aoTrocar={(valor) => {
            aoTrocar('uf', valor)
          }}
        />
      </div>
      <div className="empresa__grade empresa__grade--2">
        {texto('cidade')}
        {texto('bairro')}
      </div>
      <div className="empresa__grade empresa__grade--rua">
        {texto('logradouro')}
        {texto('numero')}
        {texto('complemento')}
      </div>
      {cep.consultando ? (
        <p className="empresa__aviso" id={idDoAviso} role="status">
          {CONSULTANDO_O_CEP}
        </p>
      ) : null}
      {cep.erro === null ? null : (
        <p className="empresa__erro" id={idDoErro} role="alert">
          {cep.erro}
        </p>
      )}
    </div>
  )
}

/** Um valor gravado que a tela mostra como campo, sem ser editável (as entregas gravadas). */
function ValorGravado({ rotulo, valor }: { rotulo: string; valor: string | null }) {
  return (
    <div className="empresa__campo">
      <dt className="empresa__rotulo">{rotulo}</dt>
      <dd className="empresa__valor">{valor ?? ''}</dd>
    </div>
  )
}

/**
 * Um endereço de entrega **gravado**, no bloco tracejado do protótipo v2: os mesmos campos do
 * endereço, em leitura — o serviço não tem rota de edição de entrega; o que há é acrescentar e
 * remover —, e "remover" à direita, em dois passos, porque remover não se desfaz.
 */
function EntregaGravada({
  entrega,
  posicao,
  confirmando,
  removendo,
  aoPedirRemocao,
  aoConfirmar,
  aoCancelar,
}: {
  entrega: EnderecoDeEntrega
  posicao: number
  confirmando: boolean
  removendo: boolean
  aoPedirRemocao: () => void
  aoConfirmar: () => void
  aoCancelar: () => void
}) {
  const e = entrega.endereco
  const titulo = `Endereço de entrega ${String(posicao + 1)}`
  return (
    <li className="empresa__entrega" role="group" aria-label={`${titulo} — ${entrega.rotulo}`}>
      <div className="empresa__entrega-cabecalho">
        <span className="empresa__entrega-titulo">{titulo}</span>
        {confirmando ? (
          <div
            className="empresa__acoes"
            role="group"
            aria-label={`Confirmar a remoção de ${entrega.rotulo}`}
          >
            <button
              type="button"
              className="empresa__botao empresa__botao--perigo"
              disabled={removendo}
              onClick={aoConfirmar}
            >
              Confirmar remoção
            </button>
            <button
              type="button"
              className="empresa__botao empresa__botao--discreto empresa__botao--pequeno"
              onClick={aoCancelar}
            >
              Cancelar
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="empresa__acao empresa__acao--perigo"
            aria-label={`Remover o endereço de entrega ${entrega.rotulo}`}
            onClick={aoPedirRemocao}
          >
            remover
          </button>
        )}
      </div>
      <dl className="empresa__entrega-campos">
        <div className="empresa__grade empresa__grade--endereco">
          <ValorGravado rotulo={ROTULOS_DO_ENDERECO.cep} valor={cepNaTela(e.cep)} />
          <ValorGravado rotulo={ROTULOS_DO_ENDERECO.uf} valor={e.uf} />
        </div>
        <div className="empresa__grade empresa__grade--2">
          <ValorGravado rotulo={ROTULOS_DO_ENDERECO.cidade} valor={e.cidade} />
          <ValorGravado rotulo={ROTULOS_DO_ENDERECO.bairro} valor={e.bairro} />
        </div>
        <div className="empresa__grade empresa__grade--rua">
          <ValorGravado rotulo={ROTULOS_DO_ENDERECO.logradouro} valor={e.logradouro} />
          <ValorGravado rotulo={ROTULOS_DO_ENDERECO.numero} valor={e.numero} />
          <ValorGravado rotulo={ROTULOS_DO_ENDERECO.complemento} valor={e.complemento} />
        </div>
        <ValorGravado rotulo="Rótulo" valor={entrega.rotulo} />
      </dl>
    </li>
  )
}

/**
 * Os endereços de entrega (11.7): os blocos tracejados do protótipo v2, **+ Adicionar endereço
 * de entrega** em texto, e "remover" em cada bloco. Cada ato é uma rota própria
 * (`editar_empresa`), e não parte do `PUT` da ficha: por isso eles não esperam o "Salvar". Os
 * campos moram dentro do `<form>` da ficha, e por isso o Enter aqui acrescenta a entrega em vez
 * de enviar a ficha.
 */
function EnderecosDeEntrega({ empresa }: { empresa: Empresa }) {
  const acrescentar = useAcrescentarEnderecoDeEntrega(empresa.id)
  const remover = useRemoverEnderecoDeEntrega(empresa.id)
  const [aberto, setAberto] = useState(false)
  const [rotulo, setRotulo] = useState('')
  const [endereco, setEndereco] = useState<EnderecoNoRascunho>(ENDERECO_VAZIO)
  const [erro, setErro] = useState<string | null>(null)
  /** A entrega cuja remoção espera o "Confirmar remoção" — remover não se desfaz. */
  const [aRemover, setARemover] = useState<string | null>(null)

  function adicionar() {
    // Enter repetido (ou clique) com o POST ainda em voo não acrescenta a mesma entrega duas vezes.
    if (acrescentar.isPending) {
      return
    }
    setErro(null)
    acrescentar.mutate(
      { rotulo: ouNulo(rotulo), endereco: corpoDoEndereco(endereco) },
      {
        onSuccess: () => {
          setAberto(false)
          setRotulo('')
          setEndereco(ENDERECO_VAZIO)
        },
        onError: (falha: unknown) => {
          setErro(mensagemDe(falha))
        },
      },
    )
  }

  /**
   * O Enter aqui **acrescenta a entrega**, e não envia a ficha: os campos moram dentro do `<form>`
   * da ficha, e o `preventDefault` cancela a submissão implícita dele.
   */
  function teclar(evento: KeyboardEvent<HTMLInputElement>) {
    if (evento.key === 'Enter') {
      evento.preventDefault()
      adicionar()
    }
  }

  const entregas = empresa.enderecos_de_entrega
  return (
    <div className="empresa__entregas" role="group" aria-label="Endereços de entrega">
      {entregas.length === 0 ? null : (
        <ul className="empresa__lista-de-entregas">
          {entregas.map((entrega, posicao) => (
            <EntregaGravada
              key={entrega.id}
              entrega={entrega}
              posicao={posicao}
              confirmando={aRemover === entrega.id}
              removendo={remover.isPending}
              aoPedirRemocao={() => {
                setErro(null)
                setARemover(entrega.id)
              }}
              aoConfirmar={() => {
                setErro(null)
                remover.mutate(entrega.id, {
                  onSuccess: () => {
                    setARemover(null)
                  },
                  onError: (falha: unknown) => {
                    setErro(mensagemDe(falha))
                  },
                })
              }}
              aoCancelar={() => {
                setARemover(null)
              }}
            />
          ))}
        </ul>
      )}

      {aberto ? (
        <div className="empresa__entrega" role="group" aria-label="Novo endereço de entrega">
          <div className="empresa__entrega-cabecalho">
            <span className="empresa__entrega-titulo">
              {`Endereço de entrega ${String(entregas.length + 1)}`}
            </span>
            <button
              type="button"
              className="empresa__acao empresa__acao--perigo"
              onClick={() => {
                setAberto(false)
                setErro(null)
              }}
            >
              Cancelar
            </button>
          </div>
          <CamposDoEndereco
            prefixo="entrega"
            endereco={endereco}
            aoTeclar={teclar}
            aoTrocar={(parte, valor) => {
              setEndereco((atual) => ({ ...atual, [parte]: valor }))
            }}
            // Na entrega nova não há o que confirmar: tudo o que a pessoa digitou ali é recente,
            // e o CEP só preenche o que está vazio.
            aoTrazerDoCep={(trazido) => {
              setEndereco((atual) => aplicarNoEndereco(atual, trazido, false))
            }}
          />
          {/* O rótulo do serviço no lugar da "referência para o motorista" do protótipo, que o
              serviço não tem. */}
          <Texto
            id="entrega-rotulo"
            rotulo="Rótulo"
            obrigatorio
            valor={rotulo}
            maxLength={TAMANHO_DO_ROTULO_DA_ENTREGA}
            placeholder="ex.: galpão ao lado do silo, entrada pela estrada de terra"
            aoTeclar={teclar}
            aoTrocar={setRotulo}
          />
          <div className="empresa__acoes">
            <button
              type="button"
              className="empresa__botao"
              disabled={acrescentar.isPending}
              onClick={adicionar}
            >
              {acrescentar.isPending ? 'Adicionando…' : 'Adicionar'}
            </button>
          </div>
        </div>
      ) : (
        <div className="empresa__linha-de-acao">
          <button
            type="button"
            className="empresa__acao"
            onClick={() => {
              setAberto(true)
            }}
          >
            {ADICIONAR_ENTREGA}
          </button>
          <span className="empresa__ajuda">{PODE_TER_MAIS_DE_UM}</span>
        </div>
      )}

      {/* Verbatim: é o serviço que explica o rótulo que faltou ou o CEP torto. */}
      {erro === null ? null : (
        <p className="empresa__erro" role="alert">
          {erro}
        </p>
      )}
    </div>
  )
}

/** De qual consulta veio a pergunta — ela aparece no cartão do campo que a disparou. */
type OrigemDaConsulta = 'cnpj' | 'cep'

/**
 * As perguntas pendentes, **uma fila por origem**: a resposta do CEP não descarta a pergunta que o
 * CNPJ tinha deixado, e vice-versa. Cada uma guarda só os campos ainda em conflito.
 */
type Pendentes = Record<OrigemDaConsulta, Trazido | null>

const SEM_PENDENTES: Pendentes = { cnpj: null, cep: null }

/** Tira um campo das perguntas pendentes — ele foi editado, e a pergunta sobre ele caducou. */
function semOCampo(pendentes: Pendentes, campo: CampoConsultavel): Pendentes {
  const limpar = (fila: Trazido | null): Trazido | null => {
    if (fila === null || !(campo in fila)) {
      return fila
    }
    const resto = Object.fromEntries(
      Object.entries(fila).filter(([chave]) => chave !== campo),
    ) as Trazido
    return Object.keys(resto).length === 0 ? null : resto
  }
  return { cnpj: limpar(pendentes.cnpj), cep: limpar(pendentes.cep) }
}

/**
 * O formulário em si — montado **só** quando tudo o que ele precisa já existe.
 *
 * `empresa` chega resolvida (ou ausente, no modo de criação): é por isso que o rascunho pode nascer
 * dela no `useState`, sem efeito de cópia e sem janela em que a digitação descarta a ficha.
 */
function Ficha({
  empresa,
  opcoes,
  rotulosForaDaLista,
  listasIndisponiveis,
  listasCarregando,
  definicoes,
  camposIndisponiveis,
  camposDesativados,
  pessoas,
  responsavel,
  aoSalvar,
  aoEncontrarFicha,
  valoresIniciais,
  responsavelInicial,
  aoCancelar,
  aoMudarEnvio,
  aoAdotarContato,
  semCartaoDePessoas = false,
}: {
  empresa?: Empresa
  opcoes: OpcoesDeDominio
  rotulosForaDaLista: ReadonlyMap<string, string>
  listasIndisponiveis: boolean
  listasCarregando: boolean
  definicoes: readonly DefinicaoDeCampo[]
  camposIndisponiveis: boolean
  /** `definicao_id -> nome` das definições **desativadas** que a ficha ainda carrega (FR-67). */
  camposDesativados: ReadonlyMap<string, string>
  pessoas?: ReactNode
  /** O campo Responsável da edição (a troca da 2.5), na grade do primeiro cartão. */
  responsavel?: ReactNode
  aoSalvar?: (empresa: Empresa, desfecho?: DesfechoDaFicha) => void
  aoEncontrarFicha?: (empresaId: string) => void
  valoresIniciais?: ValoresIniciais
  responsavelInicial?: string
  aoCancelar?: () => void
  aoMudarEnvio?: (enviando: boolean) => void
  aoAdotarContato?: () => void
  semCartaoDePessoas?: boolean
}) {
  const { data: sessao } = useSessao()
  /*
   * DW-459 (13.11c): só o escopo `todas` cadastra Empresa para outra pessoa (D-14 da 13.11a). Pelo
   * **escopo**, e não pelo nome do perfil (AD-18); a sessão ainda sem escopo conta como "não".
   */
  const escolheOResponsavel = sessao?.escopo_de_carteira === 'todas'
  /* 13.14a (DW-509, DW-507): o CNPJ/CPF somente leitura para quem não pode trocá-lo. */
  const bloqueio = empresa === undefined ? null : bloqueioDoDocumento(empresa, sessao)
  const ehCelular = useEhCelular()
  const editando = empresa !== undefined
  /* Com `aoCancelar` a ficha está num modal (o "Criar nova empresa" do Novo negócio, 13.1). */
  const noModal = aoCancelar !== undefined

  const [rascunho, setRascunho] = useState<Rascunho>(() =>
    empresa
      ? rascunhoDa(empresa)
      : {
          ...RASCUNHO_VAZIO,
          nome_fantasia: valoresIniciais?.nome_fantasia ?? '',
          documento: valoresIniciais?.documento ?? '',
          telefone: valoresIniciais?.telefone ?? '',
        },
  )
  /* O Responsável da ficha nova no modal: começa no do Negócio e é editável (13.1). */
  const [responsavelEscolhido, setResponsavelEscolhido] = useState(responsavelInicial ?? '')
  /*
   * O rascunho **de agora**, para quem precisa decidir fora da renderização — a resposta de uma
   * consulta que chega depois de a pessoa ter digitado mais. Os conflitos são calculados contra
   * ele, e não contra o `rascunho` que o retorno da mutação capturou quando saiu (esse já pode
   * estar velho). Ele só muda por `atualizar`, junto com o estado — nunca durante a renderização.
   */
  const rascunhoDeAgora = useRef(rascunho)
  const atualizar = (mudar: (atual: Rascunho) => Rascunho) => {
    const novo = mudar(rascunhoDeAgora.current)
    rascunhoDeAgora.current = novo
    setRascunho(novo)
  }

  /** Os valores dos campos personalizados como a ficha **gravada** os tem — a base do delta. */
  /*
   * O nome fantasia que a ficha tinha **ao abrir** (13.7) — e depois de cada salvar, o que o servidor
   * devolveu. É a base do corpo do `PUT`, e não a consulta viva: uma releitura da Empresa com a ficha
   * aberta (aplicar etiqueta, Reenviar, entrega) traria o nome que a adoção acabou de gravar, e o
   * rascunho vazio, comparado com ele, mandaria `null` e o apagaria.
   */
  const [nomeFantasiaGravado, setNomeFantasiaGravado] = useState<string | null>(
    () => empresa?.nome_fantasia ?? null,
  )
  const [camposGravados, setCamposGravados] = useState<Record<string, string>>(() =>
    empresa ? rascunhoDa(empresa).campos : {},
  )
  const [erro, setErro] = useState<string | null>(null)
  /** A mensagem geral do último `409` `pessoas_com_problema` — o rodapé que fala das linhas. */
  const mensagemDasPessoas = useRef<string | null>(null)
  /**
   * Editar ou responder uma linha tira do rodapé só o erro **das Pessoas** (13.14a): a recusa da
   * própria Empresa (o CNPJ, a opção) continua até o próximo envio.
   */
  function limparOErroDasPessoas() {
    if (
      erro !== null &&
      (erro === PESSOA_SEM_NOME ||
        erro === PESSOA_SEM_TELEFONE ||
        erro === PESSOAS_A_CORRIGIR ||
        erro === mensagemDasPessoas.current)
    ) {
      setErro(null)
    }
  }
  /*
   * A recusa da **consulta ao ERP** mora num estado próprio, e não no `erro` do envio.
   *
   * Elas falam de coisas diferentes e acontecem em momentos diferentes: uma é sobre o documento que
   * acabou de perder o foco, a outra é sobre a ficha que se tentou gravar. Numa variável só elas se
   * atropelam — clicar em "Cadastrar empresa" tira o foco do documento, então a consulta e o envio
   * saem **juntos**, e a que respondesse por último apagaria a mensagem da outra. Separadas, a
   * recusa do ERP fica ao lado do campo de que ela fala, e a do envio ao lado do botão.
   */
  const [erroDoErp, setErroDoErp] = useState<string | null>(null)
  /** O erro vermelho da BrasilAPI no campo do CNPJ — nunca trava o salvar (AD-28). */
  const [erroDoCnpj, setErroDoCnpj] = useState<string | null>(null)
  const [pendentes, setPendentes] = useState<Pendentes>(SEM_PENDENTES)
  const [salvo, setSalvo] = useState(false)

  /*
   * As Pessoas da ficha nova (13.2). As linhas só aparecem com `editar_empresa` (ou com a sessão
   * sem o mapa de permissões): é conveniência de tela — o `403` da rota continua tratado na linha.
   */
  /*
   * A convenção do SPA (`FichaDoNegocio.tsx`): sem sessão ainda, nada se decide — o cartão fica
   * neutro até ela chegar. Com a sessão, `editar_empresa` no mapa, ou o mapa ausente (o padrão do
   * FR-61-A: sem mapa, valem as permissões de fábrica do perfil).
   */
  const sessaoChegou = sessao !== undefined
  /* O tipo diz que o mapa sempre vem; a sessão antiga em cache pode chegar sem ele. */
  const mapaDePermissoes: object | undefined = sessao?.permissoes
  const podeCadastrarPessoas =
    sessaoChegou && (mapaDePermissoes === undefined || PERMISSAO_DAS_PESSOAS in mapaDePermissoes)
  /* Aplicar e retirar etiqueta seguem a mesma `editar_empresa` (13.6, D-3) — conveniência, e não
     autorização: quem recusa é o serviço. */
  const podeEditarAsEtiquetas = podeCadastrarPessoas
  /*
   * 13.18 (chamado de 26/10, E): a ficha nova escolhe as etiquetas de Empresa (com
   * `editar_empresa`) e um Grupo que já existe (com `editar_empresa` **e**
   * `gerir_grupos_de_empresas`). A Empresa nasce com eles na mesma transação.
   */
  const podeEscolherOGrupo =
    podeEditarAsEtiquetas &&
    (mapaDePermissoes === undefined || PERMISSAO_DOS_GRUPOS in mapaDePermissoes)
  const [etiquetasDaNova, setEtiquetasDaNova] = useState<string[]>([])
  const [grupoDaNova, setGrupoDaNova] = useState<string | null>(null)
  /* O texto do campo Grupo sem escolha: bloqueia o envio, com a recusa no campo. */
  const [textoDoGrupo, setTextoDoGrupo] = useState('')
  const [recusaDoGrupo, setRecusaDoGrupo] = useState<string | null>(null)
  /* O campo Grupo: a recusa leva o foco a ele (e o rola para a vista), longe do botão do rodapé. */
  const campoDoGrupo = useRef<HTMLInputElement>(null)
  const [pessoasNovas, setPessoasNovas] = useState<PessoaDaFichaNova[]>([])
  /* As linhas **de agora**, para a resposta da consulta ao ERP, que chega depois do `blur`. */
  const pessoasDeAgora = useRef<PessoaDaFichaNova[]>([])
  const atualizarPessoas = (mudar: (atuais: PessoaDaFichaNova[]) => PessoaDaFichaNova[]) => {
    const novas = mudar(pessoasDeAgora.current)
    pessoasDeAgora.current = novas
    setPessoasNovas(novas)
  }
  const contadorDeLinhas = useRef(0)
  /* O ERP achou a ficha deste documento e há linhas digitadas: a tela não navega sozinha (D-10). */
  const [fichaEncontrada, setFichaEncontrada] = useState<string | null>(null)
  /*
   * O "enviando" **síncrono** da cadeia inteira, da Empresa à última Pessoa: o duplo clique chega
   * antes de qualquer renderização, e só um `ref` o barra. O estado espelha o `ref` para a tela.
   */
  const enviando = useRef(false)
  const [enviandoAgora, setEnviandoAgora] = useState(false)
  /* Quantas linhas de Pessoa **adotaram** a Pessoa solta do WhatsApp (13.4), na resposta. */
  const contatosAdotados = useRef(0)
  const desfecho = (): DesfechoDaFicha => ({ contatoAdotado: contatosAdotados.current > 0 })
  /*
   * Se a ficha ainda está montada. Cancelar, navegar ou fechar o modal no meio do envio desmonta a
   * ficha, e `aoSalvar` não é chamado sobre uma tela que já não existe.
   */
  const montado = useRef(true)
  useEffect(() => {
    montado.current = true
    return () => {
      montado.current = false
    }
  }, [])
  const marcarEnvio = (valor: boolean) => {
    enviando.current = valor
    if (montado.current) {
      setEnviandoAgora(valor)
      aoMudarEnvio?.(valor)
    }
  }

  /**
   * Roda um trecho da cadeia debaixo do `enviando`, e o solta **sempre** — num `finally`, para que
   * uma exceção inesperada não deixe a ficha presa em "Salvando…".
   */
  async function emCadeia(trecho: () => Promise<void>) {
    marcarEnvio(true)
    try {
      await trecho()
    } catch (falha) {
      if (montado.current) {
        setErro(mensagemDe(falha))
      }
    } finally {
      marcarEnvio(false)
    }
  }

  const criacao = useCriarEmpresa()
  const edicao = useEditarEmpresa(empresa?.id ?? '')
  const mutacao = editando ? edicao : criacao

  const consultaNoErp = useConsultarDocumento()
  const consultaDoCnpj = useConsultarCnpj()
  /*
   * O último documento que **foi** ao ERP, para não perguntar duas vezes a mesma coisa.
   *
   * Ele guarda os dígitos, e não o texto digitado: `12.345.678/0001-99` e `12345678000199` são o
   * mesmo documento, e comparar o texto cru mandaria a segunda forma à rede de novo. Sair e voltar
   * ao campo sem mudar nada não é um pedido novo — é o mesmo pedido, e repeti-lo gastaria uma ida
   * ao ERP por clique fora do campo.
   */
  const [documentoConsultado, setDocumentoConsultado] = useState<string | null>(null)
  /*
   * Os dígitos cuja consulta ao ERP **falhou** — o ERP fora do ar, a configuração ausente.
   *
   * Eles são um terceiro estado, e não a ausência do anterior: "ainda não perguntei" e "perguntei e
   * o Carga Máquina não respondeu" levam a decisões opostas no envio. No primeiro, cadastrar seria
   * criar a ficha sem saber se a empresa já está no Carga Máquina; no segundo, **não** cadastrar
   * seria travar a tela justamente no caminho que o plano B do FR-72 existe para manter aberto.
   */
  const [documentoSemErp, setDocumentoSemErp] = useState<string | null>(null)
  /*
   * Os dígitos cuja consulta respondeu **`409`** — a ficha vinculada existe e a policy a esconde de
   * quem consulta (13.1). Um quarto estado, e o único que **trava** o cadastro: tratá-lo como "ERP
   * fora do ar" liberaria um `POST` que criaria a segunda ficha da mesma empresa. Mudar o documento
   * destrava, porque a recusa era daquele CNPJ.
   */
  const [documentoTravado, setDocumentoTravado] = useState<string | null>(null)
  /*
   * A mensagem daquele `409`, **guardada** depois que o documento muda (13.1). Apagar o CNPJ
   * destrava o cadastro sem documento — o FR-3 aceita —, mas quem cadastra precisa continuar
   * sabendo que a ficha daquela empresa existe: ela fica na tela como aviso, e não como recusa.
   */
  const [avisoDaFichaInvisivel, setAvisoDaFichaInvisivel] = useState<string | null>(null)
  /**
   * O último CNPJ que foi à BrasilAPI — pela mesma razão. Na edição, nasce com o documento gravado:
   * lá a consulta só sai quando o CNPJ mudou. "Não encontrado" conta como consultado;
   * "indisponível" não — sair e voltar ao campo tenta de novo.
   */
  const [cnpjConsultado, setCnpjConsultado] = useState<string | null>(
    () => empresa?.documento ?? null,
  )

  /*
   * O documento que está no campo **agora** — e não o que estava quando a consulta saiu.
   *
   * Ele é um `ref`, e precisa ser: os retornos das mutações fecham sobre o `rascunho` da
   * renderização em que foram criados, então comparar com aquele `rascunho` compararia os dígitos
   * consultados com eles mesmos, e a resposta obsoleta nunca seria descartada. Quem o mantém em
   * dia é `trocar`, e não uma escrita durante a renderização (`react-hooks/refs` a proíbe).
   */
  const documentoNoCampo = useRef(rascunho.documento)

  const travado =
    !editando &&
    documentoTravado !== null &&
    somenteDigitos(rascunho.documento) === documentoTravado

  const trocar = <Campo extends keyof Rascunho>(campo: Campo, valor: Rascunho[Campo]) => {
    // Qualquer alteração invalida a confirmação anterior **e** a recusa anterior do envio: os dois
    // avisos falam do que está no servidor agora, e não do que estava antes de a pessoa mexer.
    setSalvo(false)
    setErro(null)
    if (campo === 'documento') {
      documentoNoCampo.current = valor as string
      setErroDoErp(null)
      setErroDoCnpj(null)
      // O aviso de ficha encontrada era daquele documento (D-10).
      setFichaEncontrada(null)
      // O documento mudou: a pergunta que a consulta **dele** deixou caducou inteira.
      setPendentes((atuais) => ({ ...atuais, cnpj: null }))
    }
    if (campo === 'razao_social') {
      setPendentes((atuais) => semOCampo(atuais, 'razao_social'))
    }
    if (campo === 'nome_fantasia') {
      setPendentes((atuais) => semOCampo(atuais, 'nome_fantasia'))
    }
    atualizar((atual) => ({ ...atual, [campo]: valor }))
  }

  const trocarNoEndereco = (parte: ParteDoEndereco, valor: string) => {
    setSalvo(false)
    setErro(null)
    // Editar à mão um campo que a pergunta pendente queria sobrescrever encerra a pergunta dele.
    setPendentes((atuais) => semOCampo(atuais, parte))
    atualizar((atual) => ({ ...atual, endereco: { ...atual.endereco, [parte]: valor } }))
  }

  /**
   * Aplica o que uma consulta pública trouxe, contra o rascunho **de agora**: os campos vazios são
   * preenchidos na hora; o que já tinha outro valor vira a pergunta daquela origem (FR-24: "o que
   * já estava digitado não é sobrescrito sem confirmação").
   */
  function trazer(trazido: Trazido, origem: OrigemDaConsulta) {
    const { rascunho: novo, conflitos } = mesclar(rascunhoDeAgora.current, trazido)
    setSalvo(false)
    atualizar(() => novo)
    setPendentes((atuais) => ({
      ...atuais,
      [origem]: Object.keys(conflitos).length === 0 ? null : conflitos,
    }))
  }

  /** "Substituir": sobrescreve os campos que ainda estão em conflito naquela origem. */
  function substituir(origem: OrigemDaConsulta) {
    const fila = pendentes[origem]
    if (fila !== null) {
      setSalvo(false)
      atualizar((atual) =>
        (Object.entries(fila) as [CampoConsultavel, string][]).reduce(
          (parcial, [campo, valor]) => comOValor(parcial, campo, valor),
          atual,
        ),
      )
    }
    setPendentes((atuais) => ({ ...atuais, [origem]: null }))
  }

  /** A BrasilAPI (AD-28): só CNPJ de 14 dígitos, e não duas vezes o mesmo. */
  function consultarNaBrasilApi(digitos: string) {
    if (digitos.length !== DIGITOS_DO_CNPJ || digitos === cnpjConsultado) {
      return
    }
    setCnpjConsultado(digitos)
    consultaDoCnpj.mutate(digitos, {
      onSuccess: (resposta) => {
        if (somenteDigitos(documentoNoCampo.current) !== digitos) {
          return
        }
        if (!resposta.encontrado) {
          if (resposta.motivo !== 'nao_encontrado') {
            // Indisponível não conta como consultado: sair e voltar ao campo tenta de novo.
            setCnpjConsultado(null)
          }
          setErroDoCnpj(erroDaConsulta(resposta.motivo, CNPJ_NAO_ENCONTRADO))
          return
        }
        setErroDoCnpj(null)
        trazer(preenchimentoDoCnpj(resposta), 'cnpj')
      },
      onError: () => {
        if (somenteDigitos(documentoNoCampo.current) !== digitos) {
          return
        }
        setCnpjConsultado(null)
        setErroDoCnpj(NAO_FOI_POSSIVEL_CONSULTAR)
      },
    })
  }

  /**
   * O `blur` do documento: primeiro o ERP (só na criação), depois a BrasilAPI (só CNPJ inédito).
   *
   * Três guardas no ERP, e cada uma corta um pedido que não deveria existir:
   *
   * - **na edição, não consulta o ERP.** A ficha já existe e já tem (ou não) o vínculo; consultar
   *   ali só poderia mandar a pessoa para outra tela no meio de uma correção. A BrasilAPI, essa
   *   sim, sai — quando o CNPJ mudou.
   * - **documento localmente inválido, não consulta.** O serviço recusaria com `400`, e gastar o
   *   prazo do ERP para descobrir o que o cliente já sabe é o oposto de uma tela que responde. E a
   *   recusa **não** aparece aqui: quem recusa o documento vazio ou torto é o envio.
   * - **o mesmo documento já consultado no ERP, não o consulta de novo** — mas a BrasilAPI, que tem
   *   a própria memória, ainda pode tentar outra vez depois de uma indisponibilidade.
   *
   * **Só `existente` e `criada` navegam**, e com as **duas** condições — a situação e o `id`: a
   * situação é o que o serviço declara, e o `id` é o que a navegação precisa. Testar só o segundo
   * faria um corpo inesperado navegar para `/empresas/undefined`; testar só o primeiro deixaria a
   * rota receber um `id` nulo. `inedito` segue no formulário, e é só então que a BrasilAPI entra.
   */
  function conferirNoErp() {
    const digitos = somenteDigitos(rascunho.documento)
    if (bloqueio !== null) {
      // O campo somente leitura não muda, e não há o que consultar (13.14a).
      return
    }
    if (editando) {
      consultarNaBrasilApi(digitos)
      return
    }
    if (digitos.length !== DIGITOS_DO_CPF && digitos.length !== DIGITOS_DO_CNPJ) {
      return
    }
    if (digitos === documentoConsultado) {
      consultarNaBrasilApi(digitos)
      return
    }
    setDocumentoConsultado(digitos)
    /* 13.14a (DW-421): o nome fantasia já digitado vai junto — a ficha que a consulta criar nasce
       com ele. */
    consultaNoErp.mutate({ documento: digitos, nomeFantasia: rascunhoDeAgora.current.nome_fantasia }, {
      onSuccess: (consulta) => {
        /*
         * **A resposta atrasada é descartada.** Entre o `blur` e a resposta a pessoa pode ter
         * corrigido um dígito — e navegar ali abriria a ficha de um CNPJ que já não está no campo,
         * levando embora tudo o que ela tinha preenchido.
         */
        if (somenteDigitos(documentoNoCampo.current) !== digitos) {
          return
        }
        setErroDoErp(null)
        setDocumentoSemErp(null)
        if (ABREM_A_FICHA.includes(consulta.situacao) && consulta.empresa_id) {
          /*
           * Com Pessoas digitadas, a tela **não** navega (D-10): sair levaria as linhas embora sem
           * aviso. Ela avisa e oferece "Abrir a ficha". O documento volta a contar como não
           * consultado — o envio não pode tomar este CNPJ por inédito e criar a segunda ficha.
           */
          //: 13.19c1 (code review): a linha com a Pessoa escolhida conta como digitada.
          if (pessoasDeAgora.current.some(linhaQueVai)) {
            setDocumentoConsultado(null)
            setFichaEncontrada(consulta.empresa_id)
            return
          }
          // Já existe: abre a Empresa existente, e a BrasilAPI nem é chamada (FR-24).
          aoEncontrarFicha?.(consulta.empresa_id)
          return
        }
        consultarNaBrasilApi(digitos)
      },
      onError: (falha: unknown) => {
        if (somenteDigitos(documentoNoCampo.current) !== digitos) {
          return
        }
        /*
         * O ERP fora do ar **não** conta como consultado: quem tentar de novo — saindo e voltando
         * ao campo — precisa de uma segunda chance, e não do silêncio de um pedido que a tela acha
         * que já fez.
         */
        setDocumentoConsultado(null)
        setErroDoErp(mensagemDe(falha))
        if (falha instanceof ErroDaApi && falha.status === 409) {
          // A ficha existe e não é visível: o cadastro fica travado, e não liberado (13.1).
          setDocumentoTravado(digitos)
          setAvisoDaFichaInvisivel(mensagemDe(falha))
          return
        }
        setDocumentoSemErp(digitos)
        // A consulta pública não depende do ERP: ela ainda ajuda a preencher (AD-28).
        consultarNaBrasilApi(digitos)
      },
    })
  }

  /**
   * A conferência local das linhas (13.14a, DW-379: "a tela confere antes"): a com conteúdo e sem
   * nome ganha `FALTA_NOME` — e, sem telefone também, `FALTA_TELEFONE` na mesma rodada (13.17); a
   * com nome e sem telefone, `FALTA_TELEFONE`; a com o telefone (pela chave) de uma linha anterior
   * ganha `telefoneRepetidoNaFicha`. Nada sai enquanto houver uma; o rodapé diz por quê — o do nome
   * ou o do telefone quando todas as recusas são de um deles, e `PESSOAS_A_CORRIGIR` na mistura.
   * Devolve se pode seguir.
   */
  function conferirAsPessoas(linhas: readonly PessoaDaFichaNova[]): boolean {
    const primeiraComAChave = new Map<string, string>()
    type Recusas = Pick<PessoaDaFichaNova, 'recusa' | 'campoDaRecusa' | 'recusaDoTelefone'>
    const recusas = new Map<string, Recusas>()
    const recusar = (
      chave: string,
      recusa: string,
      campoDaRecusa: CampoDaRecusa,
      recusaDoTelefone: string | null = null,
    ) => {
      recusas.set(chave, { recusa, campoDaRecusa, recusaDoTelefone })
    }
    /*
     * 13.19c1: a Pessoa escolhida não se confere pelos campos (a ficha nova não a edita). Primeiro
     * as escolhidas, na ordem da tela: a mesma em duas linhas (que a escolha já recusa) e duas
     * diferentes com o mesmo número são recusadas na segunda; o número de cada uma entra em
     * `primeiraComAChave`, e a linha nova com ele recebe a recusa de telefone repetido na ficha
     * (code review).
     */
    const escolhidas = new Set<string>()
    for (const linha of linhas) {
      if (linha.escolhida === null) {
        continue
      }
      const chave =
        linha.escolhida.telefone === null ? null : chaveDoTelefone(linha.escolhida.telefone)
      if (escolhidas.has(linha.escolhida.id)) {
        recusar(linha.chave, PESSOA_JA_NA_LISTA, 'nome')
      } else if (chave !== null && primeiraComAChave.has(chave)) {
        recusar(linha.chave, PESSOA_COM_TELEFONE_REPETIDO_NA_LISTA, null)
      } else if (chave !== null) {
        primeiraComAChave.set(chave, linha.escolhida.nome)
      }
      escolhidas.add(linha.escolhida.id)
    }
    for (const linha of linhas) {
      if (linha.escolhida !== null) {
        continue
      }
      if (linhaSemNome(linha.rascunho)) {
        recusar(
          linha.chave,
          FALTA_NOME,
          'nome',
          linhaSemTelefone(linha.rascunho) ? FALTA_TELEFONE : null,
        )
        continue
      }
      if (linhaSemTelefone(linha.rascunho)) {
        recusar(linha.chave, FALTA_TELEFONE, 'telefone')
        continue
      }
      const chave = chaveDoTelefone(linha.rascunho.telefone)
      if (chave === null) {
        continue
      }
      const outra = primeiraComAChave.get(chave)
      if (outra === undefined) {
        primeiraComAChave.set(chave, linha.rascunho.nome.trim())
      } else {
        recusar(linha.chave, telefoneRepetidoNaFicha(outra), 'telefone')
      }
    }
    if (recusas.size === 0) {
      return true
    }
    atualizarPessoas((atuais) =>
      atuais.map((linha) => {
        const recusa = recusas.get(linha.chave)
        return recusa === undefined ? linha : { ...linha, ...recusa, pergunta: null }
      }),
    )
    const todas = [...recusas.values()].flatMap((linha) =>
      linha.recusaDoTelefone === null ? [linha.recusa] : [linha.recusa, linha.recusaDoTelefone],
    )
    setErro(
      todas.every((recusa) => recusa === FALTA_NOME)
        ? PESSOA_SEM_NOME
        : todas.every((recusa) => recusa === FALTA_TELEFONE)
          ? PESSOA_SEM_TELEFONE
          : PESSOAS_A_CORRIGIR,
    )
    return false
  }

  /**
   * Cria a Empresa **com** as Pessoas, numa chamada só (13.14a, DW-379): o serviço grava tudo ou
   * nada. Recusada alguma linha (`409` `pessoas_com_problema`), cada uma fica vermelha com a mensagem
   * do serviço — o `indice` é traduzido para a `chave` da linha **na hora do envio** (as vazias não
   * vão) — e a do contato com outro nome ganha a pergunta. Nada mais muda na ficha.
   */
  async function criarComAsPessoas(
    corpo: Parameters<typeof criacao.mutateAsync>[0],
    linhas: readonly PessoaDaFichaNova[],
  ) {
    let criada: EmpresaCriada
    try {
      criada = await criacao.mutateAsync({
        ...corpo,
        pessoas: linhas.map((linha) =>
          // 13.19c1: a Pessoa escolhida vai só pelo `pessoa_id` — o serviço a liga sem criar outra.
          linha.escolhida !== null
            ? { pessoa_id: linha.escolhida.id }
            : {
                // `principal` não existe na linha (D-4): marcar é um ato à parte, na ficha salva.
                ...corpoDaPessoa(linha.rascunho),
                ...corpoDaResposta(linha.resposta),
              },
        ),
      })
    } catch (falha) {
      if (!montado.current) {
        return
      }
      setSalvo(false)
      setErro(mensagemDe(falha))
      const problemas = new Map(
        problemasDasPessoas(falha).map((problema) => [linhas[problema.indice]?.chave, problema]),
      )
      mensagemDasPessoas.current = problemas.size > 0 ? mensagemDe(falha) : null
      if (problemas.size > 0) {
        //: Toda linha **enviada** reflete esta resposta: a não citada sai do vermelho de antes.
        const enviadas = new Set(linhas.map((linha) => linha.chave))
        atualizarPessoas((atuais) =>
          atuais.map((linha) => {
            const problema = problemas.get(linha.chave)
            if (problema === undefined) {
              return enviadas.has(linha.chave) &&
                (linha.recusa !== null || linha.recusaDoTelefone !== null)
                ? { ...linha, ...SEM_RECUSA, pergunta: null }
                : linha
            }
            return {
              ...linha,
              recusa: problema.mensagem,
              //: 13.17: o campo vem do **código** do serviço, e nunca o Nome por padrão.
              campoDaRecusa: campoDoCodigo(problema.codigo),
              recusaDoTelefone: null,
              pergunta: problema.contato ?? null,
              resposta: null,
            }
          }),
        )
      }
      return
    }
    if (!montado.current) {
      return
    }
    setErro(null)
    setSalvo(true)
    setCamposGravados(rascunhoDa(criada).campos)
    setNomeFantasiaGravado(criada.nome_fantasia ?? null)
    contatosAdotados.current = pessoasDaCriada(criada).filter((pessoa) => foiAdotada(pessoa)).length
    if (contatosAdotados.current > 0) {
      aoAdotarContato?.()
    }
    atualizarPessoas(() => [])
    aoSalvar?.(criada, desfecho())
  }

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    if (enviando.current) {
      return
    }
    /* As linhas em branco são ignoradas (D-6); as outras — e as escolhidas (13.19c1) — vão, na
       ordem da tela. */
    const linhas = pessoasDeAgora.current.filter(linhaQueVai)

    /* O ERP achou a ficha e há linhas: quem decide é o "Abrir a ficha" do aviso (D-10). */
    if (fichaEncontrada !== null) {
      if (linhas.length === 0) {
        aoEncontrarFicha?.(fichaEncontrada)
      }
      return
    }

    const recusa = recusaLocal(rascunho) ?? recusaDosCampos(rascunho, definicoes)
    if (recusa !== null) {
      setErro(recusa)
      return
    }
    if (!conferirAsPessoas(linhas)) {
      return
    }
    /* 13.18: texto no campo Grupo sem Grupo escolhido não vai como "sem Grupo" calado. */
    if (!editando && podeEscolherOGrupo && grupoDaNova === null && textoDoGrupo.trim() !== '') {
      setRecusaDoGrupo(RECUSA_DO_TEXTO_SEM_GRUPO)
      campoDoGrupo.current?.focus()
      return
    }

    /*
     * **O cadastro não passa na frente da consulta ao ERP.** Clicar em "Cadastrar empresa" com o
     * foco no campo do documento dispara o `blur` e o `submit` no mesmo gesto: sem esta guarda, os
     * dois sairiam juntos, e um CNPJ que o ERP conhece produziria **duas** fichas — a que a consulta
     * cria já vinculada ao Carga Máquina (`criada`) e a que o `POST /empresas` cria sem vínculo.
     *
     * São três estados, e só o primeiro bloqueia:
     *
     * - **em voo, ou ainda não perguntado** — o clique dispara (ou aguarda) a consulta e não envia:
     *   quem decide o que acontece com este CNPJ é ela. No `inedito`, o segundo clique cadastra;
     * - **já consultado** — o ERP não conhece o documento, e o cadastro segue;
     * - **consultado e o ERP estava fora do ar** — o cadastro **também** segue. Travar aqui seria
     *   fechar a tela exatamente no caminho que o plano B do FR-72 existe para manter aberto.
     *
     * A **BrasilAPI não segura nada**: falha dela nunca bloqueia (AD-28).
     */
    if (!editando) {
      if (consultaNoErp.isPending || travado) {
        return
      }
      const digitos = somenteDigitos(rascunho.documento)
      // Sem documento não há o que conferir no ERP (13.1): o cadastro segue direto.
      if (digitos !== '' && digitos !== documentoConsultado && digitos !== documentoSemErp) {
        conferirNoErp()
        return
      }
    }

    const corpo = corpoDa(
      rascunho,
      camposGravados,
      definicoes,
      empresa?.telefone ?? null,
      // Na criação, `undefined`: o nome fantasia vai sempre. Na edição, o que a ficha tinha (13.7).
      editando ? nomeFantasiaGravado : undefined,
    )
    const aoTerminar = {
      onSuccess: (salva: Empresa) => {
        setErro(null)
        setSalvo(true)
        setCamposGravados(rascunhoDa(salva).campos)
        setNomeFantasiaGravado(salva.nome_fantasia ?? null)
        aoSalvar?.(salva)
      },
      // A mensagem do serviço vai **verbatim**: é ela que explica o caso.
      onError: (falha: unknown) => {
        setSalvo(false)
        setErro(mensagemDe(falha))
      },
    }

    if (editando) {
      edicao.mutate(corpo, aoTerminar)
      return
    }
    if (!sessao) {
      setErro(FALTA_RESPONSAVEL)
      return
    }
    // O Responsável da ficha nova é quem está cadastrando: distribuição é sempre humana (FR-33).
    // No modal do Novo negócio, a gestão manda o escolhido ali (começa no Responsável do Negócio,
    // 13.1); fora do escopo `todas`, sempre quem cadastra (DW-459, 13.11c) — o `cadastro` recusa
    // com `403` a ficha para outra pessoa (D-14 da 13.11a), e esconder o seletor é conveniência.
    const responsavelDaFicha = escolheOResponsavel
      ? responsavelEscolhido || sessao.usuario_id
      : sessao.usuario_id
    void emCadeia(() =>
      criarComAsPessoas(
        {
          ...corpo,
          responsavel_id: responsavelDaFicha,
          // 13.18: só com a permissão — sem ela os campos nem aparecem, e o corpo é o de sempre.
          ...(podeEditarAsEtiquetas && etiquetasDaNova.length > 0
            ? { etiqueta_ids: etiquetasDaNova }
            : {}),
          ...(podeEscolherOGrupo && grupoDaNova !== null ? { grupo_id: grupoDaNova } : {}),
        },
        linhas,
      ),
    )
  }

  const perguntaDe = (origem: OrigemDaConsulta) => {
    const fila = pendentes[origem]
    return fila === null ? null : (
      <Confirmacao
        campos={Object.keys(fila).map(
          (campo) => ROTULOS_DOS_CONSULTAVEIS[campo as CampoConsultavel],
        )}
        aoSubstituir={() => {
          substituir(origem)
        }}
        aoManter={() => {
          setPendentes((atuais) => ({ ...atuais, [origem]: null }))
        }}
      />
    )
  }

  const descricaoDoDocumento =
    [
      erroDoErp === null ? null : ID_DA_RECUSA_DO_ERP,
      erroDoCnpj === null ? null : ID_DA_RECUSA_DO_CNPJ,
      consultaNoErp.isPending ? ID_DA_CONSULTA_NO_ERP : null,
      consultaDoCnpj.isPending ? ID_DA_CONSULTA_DO_CNPJ : null,
    ]
      .filter(Boolean)
      .join(' ') || undefined

  return (
    <section
      className="empresa"
      /* Um nome só nos dois modos: "Nova empresa" é o título do primeiro cartão, e duas regiões
         com o mesmo nome confundiriam o leitor de tela (e a suíte). */
      aria-label="Ficha da empresa"
      data-modo={editando ? 'edicao' : 'criacao'}
      /* A virada de grade (duas colunas → uma) é de árvore, e não de `@media`: o CSS lê daqui. */
      data-largura={ehCelular ? 'celular' : 'computador'}
    >
      <form id={ID_DO_FORMULARIO} className="empresa__formulario" onSubmit={enviar} noValidate>
        <fieldset className="empresa__conjunto">
          <Cartao
            id="cartao-dados-basicos"
            titulo={editando ? TITULOS.edicao : TITULOS.nova}
            ajuda={NOTA_DO_CNPJ}
          >
            {/* Primeiro campo do DOM, e não só o primeiro visualmente: a posição é contrato. E é
                dele que saem as consultas, no `blur` (5.1, 11.7). Em linha inteira. */}
            <Texto
              id="campo-documento"
              rotulo="CNPJ / CPF"
              valor={rascunho.documento}
              aoTrocar={(valor) => {
                trocar('documento', valor)
              }}
              aoSairDoCampo={conferirNoErp}
              descritoPor={[
                bloqueio
                  ? ID_DO_BLOQUEIO_DO_DOCUMENTO
                  : bloqueio === null
                    ? ID_DA_AJUDA_DO_DOCUMENTO
                    : null,
                descricaoDoDocumento,
              ]
                .filter(Boolean)
                .join(' ')}
              somenteLeitura={bloqueio !== null}
              invalido={erroDoCnpj !== null}
              className="empresa__entrada empresa__entrada--documento numero"
              inputMode="numeric"
              maxLength={TAMANHO_DO_DOCUMENTO_DIGITADO}
              placeholder={PLACEHOLDER_DO_DOCUMENTO}
              autoFocus
            />
            {/* 13.14a: travado, o campo não consulta nada no `blur` — a ajuda que promete a
                consulta dá lugar à explicação do bloqueio. */}
            {/* Sem sessão (`''`), travado e calado: nem a explicação, nem a ajuda da consulta. */}
            {bloqueio ? (
              <p className="empresa__aviso" id={ID_DO_BLOQUEIO_DO_DOCUMENTO}>
                {bloqueio}
              </p>
            ) : bloqueio === null ? (
              <span className="empresa__ajuda" id={ID_DA_AJUDA_DO_DOCUMENTO}>
                {editando ? AJUDA_DO_DOCUMENTO_NA_EDICAO : AJUDA_DO_DOCUMENTO}
              </span>
            ) : null}
            {noModal && !editando && somenteDigitos(rascunho.documento) === '' ? (
              <p className="empresa__aviso">{AVISO_DE_EMPRESA_DE_OUTRO_RESPONSAVEL}</p>
            ) : null}

            {/* `role="status"` e não `role="alert"`: esperar não é um problema, e o leitor de tela
                não precisa interromper quem já está preenchendo o campo seguinte. O aviso fica
                **ligado ao campo** por `aria-describedby`, para que quem voltar ao documento ouça
                que há uma consulta em voo sobre ele — e não uma frase solta na tela. */}
            {consultaNoErp.isPending ? (
              <p className="empresa__aviso" id={ID_DA_CONSULTA_NO_ERP} role="status">
                {CONSULTANDO_O_ERP}
              </p>
            ) : null}
            {consultaDoCnpj.isPending ? (
              <p className="empresa__aviso" id={ID_DA_CONSULTA_DO_CNPJ} role="status">
                {CONSULTANDO_O_CNPJ}
              </p>
            ) : null}

            {/* A recusa da consulta ao ERP, **verbatim** e ao lado do campo de que ela fala — é aqui
                que a mensagem literal do FR-47 aparece quando o Carga Máquina está fora. `role="alert"`
                porque ela interrompe: quem digitou o CNPJ precisa saber que ele não foi conferido. E o
                formulário **continua preenchível**. */}
            {erroDoErp === null ? null : (
              <p className="empresa__erro" id={ID_DA_RECUSA_DO_ERP} role="alert">
                {erroDoErp}
              </p>
            )}
            {/* O `409` da ficha invisível, depois que o documento mudou: aviso, e não recusa. */}
            {avisoDaFichaInvisivel !== null && erroDoErp !== avisoDaFichaInvisivel ? (
              <p className="empresa__aviso" role="status">
                {avisoDaFichaInvisivel}
              </p>
            ) : null}
            {/* O ERP achou a ficha deste documento, e há Pessoas digitadas (D-10): a tela não
                navega sozinha — elas ainda não foram gravadas. */}
            {fichaEncontrada === null ? null : (
              <div className="empresa__confirmacao" role="alert">
                {/* No modal, a ficha encontrada é escolhida no Negócio — e as linhas ficam para trás. */}
              <p className="empresa__confirmacao-texto">
                {noModal ? FICHA_ENCONTRADA_COM_PESSOAS_NO_MODAL : FICHA_ENCONTRADA_COM_PESSOAS}
              </p>
                <div className="empresa__acoes">
                  <button
                    type="button"
                    className="empresa__botao"
                    onClick={() => {
                      aoEncontrarFicha?.(fichaEncontrada)
                    }}
                  >
                    {noModal ? USAR_A_FICHA_ENCONTRADA : ABRIR_A_FICHA}
                  </button>
                </div>
              </div>
            )}
            {/* O erro vermelho da BrasilAPI, no campo. O salvar continua liberado. */}
            {erroDoCnpj === null ? null : (
              <p className="empresa__erro" id={ID_DA_RECUSA_DO_CNPJ} role="alert">
                {erroDoCnpj}
              </p>
            )}

            {perguntaDe('cnpj')}

            {camposIndisponiveis ? (
              <p className="empresa__aviso" role="alert">
                {CAMPOS_INDISPONIVEIS}
              </p>
            ) : null}
            {listasIndisponiveis ? (
              <p className="empresa__aviso" role="alert">
                {LISTAS_INDISPONIVEIS}
              </p>
            ) : listasCarregando ? (
              <p className="empresa__aviso" role="status">
                {CARREGANDO_AS_LISTAS}
              </p>
            ) : null}

            {/* A grade do protótipo v2: Nome fantasia | Razão social (13.7 — no celular, a razão
                social logo abaixo), Categoria | Origem, Responsável | Setor, Descrição, e os campos
                personalizados. */}
            <div className="empresa__grade empresa__grade--2">
              <Texto
                id="campo-nome-fantasia"
                rotulo="Nome fantasia"
                valor={rascunho.nome_fantasia}
                maxLength={TAMANHO_DO_NOME_FANTASIA}
                placeholder={PLACEHOLDER_DO_NOME_FANTASIA}
                aoTrocar={(valor) => {
                  trocar('nome_fantasia', valor)
                }}
              />
              <Texto
                id="campo-razao-social"
                rotulo="Razão social"
                obrigatorio
                valor={rascunho.razao_social}
                maxLength={TAMANHO_DA_RAZAO_SOCIAL}
                aoTrocar={(valor) => {
                  trocar('razao_social', valor)
                }}
              />

              <Seletor
                id="campo-categoria"
                rotulo="Categoria"
                valor={rascunho.categoria_id}
                opcoes={opcoes.categoria}
                rotulosForaDaLista={rotulosForaDaLista}
                aoTrocar={(valor) => {
                  trocar('categoria_id', valor)
                }}
              />
              <Seletor
                id="campo-origem"
                rotulo="Origem do cliente"
                valor={rascunho.origem_id}
                opcoes={opcoes.origem_do_cliente}
                rotulosForaDaLista={rotulosForaDaLista}
                aoTrocar={(valor) => {
                  trocar('origem_id', valor)
                }}
              />

              {/* Na criação, quem cadastra (somente leitura, FR-33); na edição, o campo da troca
                  (2.5), que quem monta a tela passa — no mesmo lugar da grade. */}
              {editando ? (
                responsavel
              ) : responsavelInicial === undefined || !escolheOResponsavel ? (
                <Responsavel nomeDeQuemEstaLogado={sessao?.nome ?? ''} />
              ) : (
                <ResponsavelEscolhivel
                  valor={responsavelEscolhido || (sessao?.usuario_id ?? '')}
                  idDeQuemEstaLogado={sessao?.usuario_id ?? ''}
                  nomeDeQuemEstaLogado={sessao?.nome ?? ''}
                  aoTrocar={(valor) => {
                    setSalvo(false)
                    setErro(null)
                    setResponsavelEscolhido(valor)
                  }}
                />
              )}
              <Seletor
                id="campo-setor"
                rotulo="Setor"
                valor={rascunho.setor_id}
                opcoes={opcoes.setor}
                rotulosForaDaLista={rotulosForaDaLista}
                aoTrocar={(valor) => {
                  trocar('setor_id', valor)
                }}
              />

              <div className="empresa__campo empresa__campo--largo">
                <label className="empresa__rotulo" htmlFor="campo-descricao">
                  Descrição
                </label>
                <textarea
                  id="campo-descricao"
                  className="empresa__area"
                  maxLength={TAMANHO_DA_DESCRICAO}
                  placeholder="Escreva detalhes importantes sobre esse cliente"
                  value={rascunho.descricao}
                  onChange={(evento) => {
                    trocar('descricao', evento.target.value)
                  }}
                />
              </div>

              {definicoes.map((definicao) => (
                <CampoPersonalizado
                  key={definicao.id}
                  definicao={definicao}
                  valor={rascunho.campos[definicao.id] ?? ''}
                  aoTrocar={(valor) => {
                    trocar('campos', { ...rascunho.campos, [definicao.id]: valor })
                  }}
                />
              ))}

              {/* O valor gravado num campo **desativado** continua legível (FR-67), só não se edita:
                  o serviço recusaria a escrita nova, e o delta nunca o manda. */}
              {Object.entries(camposGravados)
                .filter(([id]) => !definicoes.some((definicao) => definicao.id === id))
                .filter(() => !camposIndisponiveis)
                .map(([id, valor]) => (
                  <div key={id} className="empresa__campo">
                    <label className="empresa__rotulo" htmlFor={`campo-desativado-${id}`}>
                      {camposDesativados.get(id) ?? CAMPO_DESATIVADO}
                    </label>
                    <input
                      id={`campo-desativado-${id}`}
                      className="empresa__entrada"
                      type="text"
                      readOnly
                      value={valor}
                    />
                  </div>
                ))}

              {/* 13.18 (chamado de 26/10, E): na ficha nova, as etiquetas de Empresa e o Grupo
                  vão no "Cadastrar empresa", e a Empresa nasce com eles — só com as permissões. */}
              {empresa === undefined && podeEditarAsEtiquetas ? (
                <div className="empresa__campo empresa__campo--largo">
                  <span className="empresa__rotulo">Etiquetas</span>
                  <EscolhaDeEtiquetas
                    selecionadas={etiquetasDaNova}
                    aoTrocar={setEtiquetasDaNova}
                    desabilitada={enviandoAgora}
                  />
                </div>
              ) : null}
              {empresa === undefined && podeEscolherOGrupo ? (
                <div className="empresa__campo empresa__campo--largo">
                  <CampoDoGrupo
                    grupoId={grupoDaNova}
                    aoEscolher={(escolhido) => {
                      setGrupoDaNova(escolhido)
                      setRecusaDoGrupo(null)
                    }}
                    aoTrocarOTexto={(texto) => {
                      setTextoDoGrupo(texto)
                      setRecusaDoGrupo(null)
                    }}
                    recusa={recusaDoGrupo}
                    refDaEntrada={campoDoGrupo}
                    desabilitado={enviandoAgora}
                  />
                </div>
              ) : null}

              {/* As etiquetas da equipe (13.6, FR-86), em Dados básicos e só na ficha que já
                  existe: gravam na hora, fora do "Salvar" — aplicar e retirar não são campos do
                  `PUT` da ficha (e não mandam nada ao Carga Máquina). */}
              {empresa === undefined ? null : (
                <div className="empresa__campo empresa__campo--largo">
                  <span className="empresa__rotulo">Etiquetas</span>
                  <EtiquetasDoAlvo
                    alvo={{ tipo: 'empresa', id: empresa.id }}
                    nomeDoAlvo={nomeExibidoDaEmpresa(empresa)}
                    etiquetaIds={etiquetaIdsDe(empresa)}
                    podeEditar={podeEditarAsEtiquetas}
                  />
                </div>
              )}
            </div>
          </Cartao>

          <Cartao id="cartao-contato" titulo={TITULOS.contato} ajuda={NOTA_DO_CONTATO}>
            {/* E-mail | Telefone · Website. O protótipo tem também WhatsApp, Celular e Ramal — o
                serviço não os tem, e a ficha não inventa campo que não grava. */}
            <div className="empresa__grade empresa__grade--2">
              <Texto
                id="campo-email"
                rotulo="E-mail"
                valor={rascunho.email}
                tipo="email"
                autoComplete="section-empresa work email"
                maxLength={TAMANHO_DO_EMAIL}
                placeholder="exemplo@email.com"
                aoTrocar={(valor) => {
                  trocar('email', valor)
                }}
              />
              <Texto
                id="campo-telefone"
                rotulo="Telefone"
                valor={rascunho.telefone}
                tipo="tel"
                autoComplete="section-empresa work tel"
                maxLength={TAMANHO_DO_TELEFONE}
                placeholder="(00) 0000-0000"
                aoTrocar={(valor) => {
                  trocar('telefone', valor)
                }}
              />
              <Texto
                id="campo-site"
                rotulo="Website"
                valor={rascunho.site}
                inputMode="url"
                maxLength={TAMANHO_DO_SITE}
                placeholder="www.exemplo.com.br"
                aoTrocar={(valor) => {
                  trocar('site', valor)
                }}
              />
            </div>
          </Cartao>

          <Cartao id="cartao-endereco" titulo={TITULOS.endereco} ajuda={NOTA_DO_ENDERECO}>
            <CamposDoEndereco
              prefixo="endereco"
              endereco={rascunho.endereco}
              aoTrocar={trocarNoEndereco}
              aoTrazerDoCep={(trazido) => {
                trazer(trazido, 'cep')
              }}
            />
            {perguntaDe('cep')}
            {empresa === undefined ? (
              <p className="empresa__ajuda">
                Os endereços de entrega entram depois de a empresa ser cadastrada.
              </p>
            ) : (
              <EnderecosDeEntrega empresa={empresa} />
            )}
          </Cartao>
        </fieldset>
      </form>

      {/* Pessoas inline (a seção da 2.2): fora do `<form>` da ficha, porque tem os próprios. Na
          ficha nova (13.2), as linhas de `PessoasDaFichaNova`, ligadas ao `<form>` por `form`.
          Sem o cartão no modal do "+ Pessoa" (13.15): a Pessoa é a que o "+ Pessoa" cadastra. */}
      {semCartaoDePessoas
        ? null
        : (pessoas ?? (
            <Cartao id="cartao-pessoas" titulo={TITULOS.pessoas} ajuda={NOTA_DAS_PESSOAS}>
              {!sessaoChegou ? null : podeCadastrarPessoas ? (
                <PessoasDaFichaNova
                  linhas={pessoasNovas}
                  ocupado={enviandoAgora}
                  formulario={ID_DO_FORMULARIO}
                  aoAdicionar={() => {
                    contadorDeLinhas.current += 1
                    const chave = `${PREFIXO_DA_LINHA}-${String(contadorDeLinhas.current)}`
                    atualizarPessoas((atuais) => [
                      ...atuais,
                      {
                        chave,
                        rascunho: PESSOA_VAZIA,
                        ...SEM_RECUSA,
                        pergunta: null,
                        resposta: null,
                        escolhida: null,
                      },
                    ])
                  }}
                  aoTrocar={(chave, campo, valor) => {
                    setSalvo(false)
                    limparOErroDasPessoas()
                    // Editar a linha limpa a recusa e a resposta dela: a pergunta era sobre o que
                    // estava.
                    atualizarPessoas((atuais) =>
                      atuais.map((linha) =>
                        linha.chave === chave
                          ? {
                              ...linha,
                              rascunho: { ...linha.rascunho, [campo]: valor },
                              ...SEM_RECUSA,
                              pergunta: null,
                              resposta: null,
                            }
                          : linha,
                      ),
                    )
                  }}
                  aoResponder={(chave, resposta) => {
                    setSalvo(false)
                    limparOErroDasPessoas()
                    atualizarPessoas((atuais) =>
                      atuais.map((linha) =>
                        linha.chave === chave
                          ? { ...linha, ...SEM_RECUSA, pergunta: null, resposta }
                          : linha,
                      ),
                    )
                  }}
                  aoDescartar={(chave) => {
                    atualizarPessoas((atuais) => atuais.filter((linha) => linha.chave !== chave))
                  }}
                  aoEscolher={(chave, pessoa) => {
                    setSalvo(false)
                    limparOErroDasPessoas()
                    atualizarPessoas((atuais) => escolherNaLinha(atuais, chave, pessoa))
                  }}
                  aoDesfazerAEscolha={(chave) => {
                    setSalvo(false)
                    limparOErroDasPessoas()
                    atualizarPessoas((atuais) => desfazerAEscolha(atuais, chave))
                  }}
                />
              ) : (
                <p className="empresa__ajuda">{PESSOAS_SEM_PERMISSAO}</p>
              )}
            </Cartao>
          ))}

      <Cartao id="cartao-redes-sociais" titulo={TITULOS.redes}>
        {/* Instagram | LinkedIn · Facebook. O "X (Twitter)" do protótipo não existe no serviço.
            Estes campos moram fora do `<form>` e se ligam a ele por `form`. */}
        <fieldset className="empresa__conjunto">
          <div className="empresa__grade empresa__grade--2">
            <Texto
              id="campo-instagram"
              rotulo="Instagram"
              valor={rascunho.instagram}
              maxLength={TAMANHO_DA_REDE_SOCIAL}
              placeholder="instagram.com/empresa"
              formulario={ID_DO_FORMULARIO}
              aoTrocar={(valor) => {
                trocar('instagram', valor)
              }}
            />
            <Texto
              id="campo-linkedin"
              rotulo="LinkedIn"
              valor={rascunho.linkedin}
              maxLength={TAMANHO_DA_REDE_SOCIAL}
              placeholder="linkedin.com/company/empresa"
              formulario={ID_DO_FORMULARIO}
              aoTrocar={(valor) => {
                trocar('linkedin', valor)
              }}
            />
            <Texto
              id="campo-facebook"
              rotulo="Facebook"
              valor={rascunho.facebook}
              maxLength={TAMANHO_DA_REDE_SOCIAL}
              placeholder="facebook.com/empresa"
              formulario={ID_DO_FORMULARIO}
              aoTrocar={(valor) => {
                trocar('facebook', valor)
              }}
            />
          </div>
        </fieldset>
      </Cartao>

      {/* O rodapé do protótipo v2: a Faixa à esquerda (no lugar do "Ranking" de estrelas, que a
          Faixa substituiu — decisão 4 da 11.7) e, à direita, Cancelar e Salvar. */}
      <section className="empresa__cartao empresa__rodape" aria-label="Salvar a ficha">
        {erro === null ? null : (
          <p className="empresa__erro" role="alert">
            {erro}
          </p>
        )}
        {salvo && erro === null ? (
          <p className="empresa__sucesso" role="status">
            {SALVO}
          </p>
        ) : null}
        <div className="empresa__rodape-linha">
          <fieldset className="empresa__conjunto empresa__faixa">
            <div className="empresa__campo">
              <label className="empresa__rotulo" htmlFor="campo-faixa">
                Faixa
              </label>
              <select
                id="campo-faixa"
                className="empresa__selecao"
                form={ID_DO_FORMULARIO}
                value={rascunho.faixa}
                onChange={(evento) => {
                  trocar('faixa', evento.target.value as Faixa | '')
                }}
              >
                <option value="">{SEM_FAIXA}</option>
                {FAIXAS.map((faixa) => (
                  <option key={faixa} value={faixa}>
                    {ROTULOS_DA_FAIXA[faixa]}
                  </option>
                ))}
              </select>
            </div>
          </fieldset>
          <div className="empresa__acoes empresa__rodape-acoes">
            {aoCancelar === undefined && !enviandoAgora ? (
              <Link to={CAMINHOS.empresas} className="empresa__botao empresa__botao--discreto">
                Cancelar
              </Link>
            ) : (
              /* No meio da cadeia o "Cancelar" não sai: na rota o link vira botão desabilitado. */
              <button
                type="button"
                className="empresa__botao empresa__botao--discreto"
                disabled={enviandoAgora}
                onClick={aoCancelar}
              >
                Cancelar
              </button>
            )}
            {/* Travado pelo `409` da consulta (13.1): no modal só sobra o Cancelar; na tela, o
                botão fica desabilitado até o documento mudar. */}
            {travado && noModal ? null : (
              <button
                className="empresa__botao"
                type="submit"
                form={ID_DO_FORMULARIO}
                disabled={mutacao.isPending || enviandoAgora || travado}
              >
                {mutacao.isPending || enviandoAgora
                  ? 'Salvando…'
                  : editando
                    ? 'Salvar'
                    : 'Cadastrar empresa'}
              </button>
            )}
          </div>
        </div>
      </section>
    </section>
  )
}

/** O cartão que ocupa o lugar do formulário enquanto ele não pode existir. */
function CartaoDeEspera({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="empresa">
      <Cartao id="cartao-espera" titulo={titulo}>
        {children}
      </Cartao>
    </section>
  )
}

/**
 * Os ids de lista de domínio que a ficha carrega e que o vocabulário ativo **não** rotula.
 *
 * Sem a ficha ou sem o vocabulário carregados a resposta é vazia: pedir o rótulo de um id antes de
 * saber se a lista já o tem gastaria uma ida à rede por campo em toda abertura.
 */
export function idsSemRotulo(
  empresa: Empresa | undefined,
  opcoes: OpcoesDeDominio | undefined,
): string[] {
  if (empresa === undefined || opcoes === undefined) {
    return []
  }
  const conhecidos = new Set(
    Object.values(opcoes).flatMap((lista) => lista.map((opcao) => opcao.id)),
  )
  const daFicha = [empresa.categoria_id, empresa.origem_id, empresa.setor_id]
  return [
    ...new Set(
      daFicha.filter((id): id is string => id !== null && id !== '' && !conhecidos.has(id)),
    ),
  ]
}

export interface PropriedadesDoFormulario {
  /** `undefined` cadastra uma ficha nova; com `id`, edita a existente. */
  empresaId?: string
  /**
   * Chamado depois de o serviço confirmar. A navegação é de quem montou a tela. Na ficha nova, o
   * `desfecho` diz se alguma linha de Pessoa **adotou** a Pessoa solta do WhatsApp (13.4), somado
   * entre as passadas da cadeia — a rota o leva à ficha aberta, que avisa.
   */
  aoSalvar?: (empresa: Empresa, desfecho?: DesfechoDaFicha) => void
  /**
   * Chamado quando a consulta ao ERP encontra (ou cria) a ficha daquele documento — a 5.1. A
   * navegação é de quem montou a tela: o formulário é montado em duas rotas.
   */
  aoEncontrarFicha?: (empresaId: string) => void
  /**
   * O cartão **Pessoas** (inline, a seção da 2.2), que quem monta a tela passa — ele tem guarda,
   * formulários e trilha próprios. Ausente (na criação), a ficha mostra as linhas da ficha nova
   * (13.2), gravadas depois da Empresa.
   */
  pessoas?: ReactNode
  /**
   * O campo **Responsável** da edição (a troca da 2.5), que quem monta a tela passa: ele mora
   * na grade do primeiro cartão, ao lado de Categoria e Setor (protótipo v2). Na criação o
   * formulário mostra quem cadastra, somente leitura, no mesmo lugar.
   */
  responsavel?: ReactNode
  /**
   * O que a ficha **nova** já traz preenchido (13.1): o "Criar nova empresa" do Novo negócio passa
   * o texto digitado — no nome fantasia desde a 13.14a (DW-420), ou no CNPJ/CPF quando é só o
   * documento — e, vindo de uma conversa de WhatsApp, o telefone do contato.
   */
  valoresIniciais?: ValoresIniciais
  /**
   * O Responsável com que a ficha nova começa, **editável** (13.1): no modal do Novo negócio, o
   * Responsável escolhido no Negócio. Ausente, o campo mostra quem cadastra, somente leitura.
   */
  responsavelInicial?: string
  /**
   * Presente, o "Cancelar" vira botão e chama isto — a ficha está num modal, e não na rota
   * `/empresas/nova` (13.1). É também o que liga o aviso de Empresa de outro responsável.
   */
  aoCancelar?: () => void
  /**
   * Avisa quem hospeda a ficha quando a cadeia de envio começa e termina (13.2): o modal do Novo
   * negócio não fecha por Esc nem pelo fundo enquanto a Empresa e as Pessoas estão a caminho.
   */
  aoMudarEnvio?: (enviando: boolean) => void
  /**
   * Chamado a cada linha de Pessoa que **adotou** a Pessoa solta do WhatsApp (13.4): o modal do
   * Novo negócio e do painel o usa para avisar mesmo quando a ficha termina pelo "Seguir sem elas",
   * que é dele e não passa por `aoSalvar`.
   */
  aoAdotarContato?: () => void
  /**
   * Sem o cartão **Pessoas** (13.15): o modal da Empresa aberto pelo "+ Pessoa" — a Pessoa é a do
   * formulário de quem abriu, e a ficha nasce só com a Empresa.
   */
  semCartaoDePessoas?: boolean
}

/** O que a ficha nova diz a quem a hospeda quando termina (13.4). */
export interface DesfechoDaFicha {
  /** Alguma linha de Pessoa completou a Pessoa solta com o mesmo telefone, em vez de criar outra. */
  contatoAdotado: boolean
}

/** Os valores com que uma ficha nova pode nascer preenchida (13.1). */
export interface ValoresIniciais {
  /**
   * O texto que a pessoa digitou no campo de Empresa e não achou (13.14a, DW-420): é quase sempre a
   * marca, e vai ao Nome fantasia — a razão social fica para o CNPJ (a consulta pública a traz).
   */
  nome_fantasia?: string
  /** O CNPJ/CPF colado no campo de Empresa (o texto sem letra, de 11 ou 14 dígitos). */
  documento?: string
  telefone?: string
}

export function FormularioDeEmpresa({
  empresaId,
  aoSalvar,
  aoEncontrarFicha,
  pessoas,
  responsavel,
  valoresIniciais,
  responsavelInicial,
  aoCancelar,
  aoMudarEnvio,
  aoAdotarContato,
  semCartaoDePessoas = false,
}: PropriedadesDoFormulario) {
  const consultaDasOpcoes = useOpcoesDeDominio()
  const consultaDaFicha = useEmpresa(empresaId)
  const consultaDosCampos = camposDaEmpresa.useCampos()
  const editando = empresaId !== undefined

  /*
   * Os valores que a ficha **usa** e que o vocabulário ativo não sabe rotular (FR-67). O hook fica
   * **antes** das portas: chamá-lo depois de um `return` mudaria a ordem dos hooks.
   */
  const idsForaDoVocabulario = idsSemRotulo(consultaDaFicha.data, consultaDasOpcoes.data)
  const rotulosForaDaLista = useRotulosDeOpcoes(idsForaDoVocabulario)

  // Só as definições ativas, em ordem. Falha ao ler o catálogo não trava a ficha: os campos não
  // aparecem, e o aviso diz por quê (`CAMPOS_INDISPONIVEIS`).
  const definicoesAtivas = (Array.isArray(consultaDosCampos.data) ? consultaDosCampos.data : [])
    .filter((definicao) => definicao.ativo)
    .sort((a, b) => a.ordem - b.ordem)
  /* Os ids que a ficha carrega e o catálogo ativo não tem — as definições desativadas (FR-67).
     Só depois de o catálogo chegar: antes disso todo id pareceria desativado. */
  const idsDesativados = consultaDosCampos.isSuccess
    ? Object.keys(consultaDaFicha.data?.campos_personalizados ?? {}).filter(
        (id) => !definicoesAtivas.some((definicao) => definicao.id === id),
      )
    : []
  const camposDesativados = useNomesDeCamposDesativados(idsDesativados)

  if (editando && consultaDaFicha.isPending) {
    return (
      <CartaoDeEspera titulo="Editar empresa">
        <p className="empresa__ajuda">{CARREGANDO_A_FICHA}</p>
      </CartaoDeEspera>
    )
  }

  if (editando && consultaDaFicha.isError) {
    return (
      <CartaoDeEspera titulo="Editar empresa">
        {/* Verbatim: o `404` do serviço é o mesmo para ficha inexistente e para ficha que a
            RLS esconde (FR-30), e é ele que sabe explicar o caso. */}
        <p className="empresa__erro" role="alert">
          {mensagemDe(consultaDaFicha.error)}
        </p>
      </CartaoDeEspera>
    )
  }

  // Só as definições ativas (a leitura já as filtra), em ordem. Falha ao ler o catálogo não
  // trava a ficha: sem ele, os campos personalizados simplesmente não aparecem.
  const definicoes = definicoesAtivas

  return (
    <Ficha
      empresa={consultaDaFicha.data}
      opcoes={consultaDasOpcoes.data ?? OPCOES_VAZIAS}
      rotulosForaDaLista={rotulosForaDaLista}
      listasIndisponiveis={consultaDasOpcoes.isError}
      listasCarregando={consultaDasOpcoes.isPending}
      definicoes={definicoes}
      camposIndisponiveis={consultaDosCampos.isError}
      camposDesativados={camposDesativados}
      pessoas={pessoas}
      responsavel={responsavel}
      aoSalvar={aoSalvar}
      aoEncontrarFicha={aoEncontrarFicha}
      valoresIniciais={valoresIniciais}
      responsavelInicial={responsavelInicial}
      aoCancelar={aoCancelar}
      aoMudarEnvio={aoMudarEnvio}
      aoAdotarContato={aoAdotarContato}
      semCartaoDePessoas={semCartaoDePessoas}
    />
  )
}
